/**
 * rt-smart-upsell.js - Rawtorque Smart Upsell Engine v2.0
 *
 * v2.0 - Fitment-first ranking (5 Sep 2026):
 *   The product-page bundle now runs the same ranking engine as the cart-page
 *   rail, adapted from "everything already in the basket" to "the one product
 *   being viewed". Reference: rt-bundle-upsell-spec.md.
 *
 *   What was wrong: the widget picked what to show from a hand-written
 *   COMPLEMENT map of product TYPES, and for anything tagged vehicle-car that
 *   map was overridden with a fitment-safe list of fluids. A GBP 130 set of
 *   Brembo pads for a Polo was therefore bundled with two bottles of Liqui
 *   Moly additive - fitment-safe, and useless. The premise behind that
 *   override ("the catalogue has no car model data") is no longer true.
 *
 *   What changed:
 *     - Fitment tags are read for cars as well as bikes, in both of the tag
 *       styles this catalogue uses ("SKODA RAPID/FABIA after 2012" and a bare
 *       "Polo"), so the primary candidate source is now a search for the
 *       anchor's own vehicle.
 *     - Scoring is the cart rail's: same vehicle +30, axle counterpart +25,
 *       per shared fitment tag +10, proven attachment +15/+10, same kind of
 *       part -45, different vehicle -25.
 *     - Front + rear of the same part for the same vehicle is promoted, not
 *       blocked: 4 of the 8 multi-item orders in the last 50 were exactly that.
 *     - Badge, class and category tags (pill-*, vehicle-*, "Brake Pads",
 *       "Performance") no longer count as fitment.
 *     - Picks are hydrated from /products/<handle>.js before render, so the
 *       bundle always holds real variant ids and real prices.
 *     - DEBUG is opt-in (?rtdebug=1) instead of always-on console noise.
 *
 * v1.8 - Intent + Collection Push:
 *   - Hard car↔bike guard: a bike product can never appear on a car page
 *     (or vice versa) - fixes JASO-MA2 motorcycle oil showing on a car
 *     cold-air-intake.
 *   - Intent-aware: performance buyers (intake/exhaust/ECU) get performance/
 *     tuning complements first, not plain maintenance oil.
 *   - Collection CTA (hybrid): every product maps to its best collection
 *     (e.g. Cold Air Intake → "Car Performance Upgrades"). Shown as an
 *     "Explore more in …" link under the bundle; when there's no confident
 *     bundle the collection becomes the primary card (solo mode).
 *
 * v1.7 - Coverage + Car Fitment:
 *   - COMPLEMENT/type maps expanded to cover the full catalogue (coolant,
 *     additives, cold air intake, wiring harness, pod filter, fog lamp,
 *     navigation, paddock stand, etc.) + fixed type-name typos
 *     ("fog lamp"→fog light, "sum guard"→sump guard).
 *   - Car awareness: car products only auto-upsell fitment-SAFE universal
 *     items (oil/coolant/additive/care) - no model-specific cross-matches
 *     (fixes the Swift-plug→Jimny-filter wrong-fit bug).
 *   - Tier-3 universal fallback: when rules underfill, fall back to Shopify's
 *     store-curated "complementary" recommendations so every product shows
 *     something relevant instead of an empty widget.
 *   - Fixed scoreOne same-family return shape + findUpsells d/data ref.
 *
 * v1.6 - Expert Salesman Mode:
 *   - BENEFIT_COPY: every upsell now shows WHY the rider needs it
 *   - Helmet → Bluetooth intercom upsell added
 *   - L0 metafield layer: admin can pin exact upsell products per product
 */
(function () {
  'use strict';

  // Opt-in tracing: add ?rtdebug=1 to the product URL, or set
  // window.RT_UPSELL_DEBUG = true before the script runs. Off for customers.
  const DEBUG = (function () {
    try {
      return /[?&]rtdebug=1/.test(location.search) || window.RT_UPSELL_DEBUG === true;
    } catch (e) { return false; }
  })();

  // RT PERF: resize Shopify-served images to the displayed box.
  // Upsell thumbs arrive from /products/{handle}.js at native size
  // (e.g. 1000x1000) and were painted into a 48px box. 48 @2x = 96.
  const rtImg = (url, w) => {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      const isShopify = /cdn\.shopify\.com$/i.test(u.hostname)
        || /myshopify\.com$/i.test(u.hostname)
        || /\/(cdn\/shop|files)\//i.test(u.pathname);
      if (!isShopify) return url;
      u.searchParams.delete('height');
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (e) { return url; }
  };

  // ── COMPLEMENT MAP ─────────────────────────────────────────────────────────
  const COMPLEMENT = {
    // Exhaust / silencer → service items that make sense post-install
    'exhaust':         ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    'silencer':        ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    'muffler':         ['spark plug', 'air filter', 'engine oil', 'oil filter'],
    // Hazard module / flash → lighting upgrades
    'hazard module':   ['fog light', 'led headlight', 'led tail light', 'indicator'],
    'flash module':    ['fog light', 'led headlight', 'led tail light', 'indicator'],
    'flasher relay':   ['fog light', 'indicator', 'led tail light'],
    // Helmet → Bluetooth intercom first, then visor
    'helmet':          ['bluetooth intercom', 'visor', 'helmet bag', 'grip set'],
    // Bluetooth intercom → suggest helmet if they don't have one
    'bluetooth intercom': ['helmet', 'grip set', 'bar end'],
    'intercom':        ['helmet', 'grip set'],
    // Service
    'spark plug':      ['air filter', 'engine oil', 'oil filter'],
    'iridium plug':    ['air filter', 'engine oil', 'oil filter'],
    'spark plugs':     ['air filter', 'engine oil', 'oil filter'],
    'air filter':      ['spark plug', 'exhaust', 'engine oil', 'oil filter'],
    'air cleaner':     ['spark plug', 'exhaust', 'engine oil', 'oil filter'],
    'oil filter':      ['engine oil', 'air filter', 'spark plug'],
    'engine oil':      ['oil filter', 'air filter', 'spark plug'],
    'gear oil':        ['engine oil', 'oil filter'],
    'brake fluid':     ['brake pad', 'brake disc'],
    'fork oil':        ['oil filter', 'fork seal'],
    // Chain / drivetrain
    'chain lube':      ['chain cleaner', 'chain', 'sprocket kit'],
    'chain lubricant': ['chain cleaner', 'chain', 'sprocket kit'],
    'chain cleaner':   ['chain lube', 'chain', 'sprocket kit'],
    'chain oil':       ['chain cleaner', 'chain lube', 'chain'],
    // Chain / sprocket kit buyer already has the drivetrain - sell maintenance
    'chain':           ['chain lubricant', 'chain cleaner'],
    'sprocket':        ['chain lubricant', 'chain cleaner'],
    'sprocket kit':    ['chain lubricant', 'chain cleaner'],
    'clutch plate':    ['clutch spring', 'clutch cable'],
    'clutch cable':    ['brake cable', 'clutch plate'],
    'brake cable':     ['clutch cable'],
    // Brakes
    'brake pad':       ['brake disc', 'brake fluid', 'brake caliper'],
    'brake pads':      ['brake disc', 'brake fluid'],
    'brake disc':      ['brake pad', 'brake fluid', 'brake caliper'],
    'brake shoe':      ['brake drum', 'brake fluid'],
    'brake drum':      ['brake shoe', 'brake fluid'],
    'brake caliper':   ['brake pad', 'brake disc', 'brake fluid'],
    // Tyres
    'tyre':            ['tube', 'tyre sealant', 'rim tape'],
    'tube':            ['tyre', 'rim tape'],
    // Lighting - fog / auxiliary light: switch harness first, then dimmer as separate slot
    'fog light':       ['switch kit', 'dimmer', 'wiring harness', 'led headlight', 'indicator', 'handlebar', 'grip set'],
    'auxiliary light': ['switch kit', 'dimmer', 'wiring harness', 'led headlight', 'indicator', 'handlebar', 'grip set'],
    'led pod':         ['switch kit', 'dimmer', 'wiring harness', 'fog light', 'indicator', 'handlebar', 'grip set'],
    // Switch kit / dimmer - reciprocal
    'switch kit':      ['fog light', 'auxiliary light', 'led headlight', 'indicator', 'dimmer'],
    'dimmer':          ['fog light', 'auxiliary light', 'led pod', 'switch kit'],
    'indicator':       ['headlight', 'tail light', 'fog light'],
    'headlight':       ['indicator', 'tail light', 'fog light'],
    'led headlight':   ['indicator', 'tail light', 'fog light'],
    'tail light':      ['indicator', 'headlight'],
    'led tail light':  ['indicator', 'headlight'],
    'led kit':         ['headlight', 'indicator', 'tail light'],
    'turn signal':     ['headlight', 'tail light', 'hazard module'],
    'battery':         ['battery charger', 'spark plug'],
    // Guards
    'crash guard':     ['engine guard', 'saree guard', 'grip set', 'bar end', 'tank pad'],
    'engine guard':    ['crash guard', 'sump guard', 'grip set', 'bar end'],
    'frame slider':    ['crash guard', 'engine guard', 'bar end'],
    'sump guard':      ['engine guard', 'crash guard'],
    'saree guard':     ['crash guard', 'engine guard'],
    'leg guard':       ['saree guard', 'crash guard'],
    'tank guard':      ['tank pad', 'crash guard', 'grip set'],
    'tank pad':        ['tank guard', 'seat cover', 'grip set'],
    'seat cover':      ['grip set', 'tank pad'],
    // Ergonomics
    'handlebar':       ['grip set', 'bar end', 'mirror'],
    'grip':            ['handlebar', 'bar end', 'lever set'],
    'grip set':        ['handlebar', 'bar end', 'lever set'],
    'bar end':         ['grip set', 'handlebar', 'mirror'],
    'mirror':          ['grip set', 'handlebar', 'indicator'],
    'lever set':       ['grip set', 'brake fluid', 'clutch cable'],
    // Suspension - upgrade-mode buyer: show performance upgrades, then protection
    'suspension':      ['exhaust', 'air filter', 'chain', 'crash guard', 'engine guard', 'fork oil', 'engine oil'],
    'fork':            ['fork oil', 'engine oil', 'brake fluid'],
    // Phone holder / mounts
    'phone holder':    ['grip set', 'bar end', 'handlebar', 'usb charger'],
    'phone mount':     ['grip set', 'handlebar', 'usb charger'],
    // Windscreen / windshield
    'windscreen':      ['crash guard', 'engine guard', 'grip set'],
    'windshield':      ['crash guard', 'engine guard', 'grip set'],
    // Radiator guard
    'radiator guard':  ['engine guard', 'crash guard', 'coolant'],
    // Fuel tuner / ECU / autotune - performance upgrade buyer
    'fuel tuner':      ['exhaust', 'air filter', 'spark plug'],
    'autotune':        ['exhaust', 'air filter', 'spark plug'],
    'ecu':             ['exhaust', 'air filter', 'spark plug'],
    'fuel module':     ['exhaust', 'air filter', 'spark plug'],
    'swing arm stand': ['chain lubricant', 'chain cleaner', 'chain'],
    'rearset':         ['footpegs', 'lever set', 'grip set'],
    // Electrical / lighting coverage gaps
    'wiring harness':  ['switch kit', 'fog light', 'led headlight', 'dimmer'],
    'fog lamp':        ['switch kit', 'dimmer', 'wiring harness', 'led headlight'],
    'led light':       ['switch kit', 'dimmer', 'wiring harness'],
    // Filter naming gaps
    'pod filter':      ['spark plug', 'engine oil', 'exhaust', 'air filter'],
    'cold air intake': ['air filter', 'spark plug', 'engine oil', 'ecu'],
    // Service / fluids - also the safe universal set for cars
    'coolant':         ['engine oil', 'brake fluid', 'additive', 'car care'],
    'additive':        ['engine oil', 'air filter', 'spark plug', 'coolant'],
    'fuel optimizer':  ['engine oil', 'air filter', 'spark plug'],
    'engine treatment':['engine oil', 'oil filter', 'additive'],
    'transmission fluid': ['engine oil', 'coolant'],
    'hydraulic fluid': ['brake fluid', 'engine oil'],
    // Care products
    'car care':        ['cleaner', 'engine oil', 'coolant'],
    'cleaner':         ['car care', 'engine oil', 'chain lube'],
    'helmet care':     ['visor', 'helmet bag', 'bluetooth intercom'],
    // Guard typo + misc fitment-safe
    'sum guard':       ['engine guard', 'crash guard', 'grip set'],
    'navigation':      ['bluetooth intercom', 'phone mount', 'grip set'],
    'paddock stand':   ['chain lubricant', 'chain cleaner', 'chain', 'sprocket kit'],
    'handlebar riser': ['grip set', 'bar end', 'mirror'],
  };

  // Title keyword → inferred product type (when product_type field is blank)
  const TITLE_TYPES = [
    ['exhaust',        ['exhaust']],
    ['silencer',       ['silencer', 'muffler', 'slip-on', 'slip on']],
    ['hazard module',  ['hazard flash', 'flashx', 'flash module', 'hazard module', 'flasher relay', 'blinker relay']],
    ['switch kit',     ['switch kit', 'wiring harness', 'relay harness', 'switch harness', 'led switch', 'rocker switch', 'toggle switch', 'switch relay', 'harness kit']],
    ['dimmer',         ['dimmer', 'brightness controller', 'brightness control', 'quad-level', 'quad level']],
    // v2.0: intercom and visor are matched BEFORE helmet, and 'helmet' is now
    // a keyword in its own right. A title like "Axor X-Cross Dual Visor Helmet"
    // used to infer as a visor, which made a second helmet look like an
    // accessory for the first one.
    ['bluetooth intercom', ['bluetooth intercom', 'intercom', 'cardo', 'sena ', 'fodsports', 'b4fm', 'b4plus', 'helmet communication', 'helmet headset', 'motorcycle intercom', 'rider communication', 'helmet speaker', 'helmet bluetooth']],
    ['visor',          ['visor', 'face shield', 'helmet shield', 'helmet visor']],
    ['helmet bag',     ['helmet bag', 'helmet carry', 'helmet lock', 'helmet holder']],
    ['helmet',         ['full face helmet', 'half face helmet', 'open face helmet', 'modular helmet', 'flip up helmet', 'motocross helmet', 'off road helmet', 'smk', 'studds helmet', 'steelbird helmet', 'vega helmet', 'ls2 helmet', 'arai helmet', 'shoei helmet', 'helmet']],
    ['suspension',     ['suspension', 'air born', 'air-born', 'shock absorber', 'monoshock', 'rear shock', 'front fork suspension', 'performance suspension', 'adjustable suspension']],
    ['fork',           ['fork oil', 'fork seal', 'fork leg', 'fork tube']],
    ['phone holder',   ['phone holder', 'phone mount', 'mobile holder', 'mobile mount', 'smartphone holder', 'phone clamp']],
    ['windscreen',     ['windscreen', 'windshield', 'visor screen', 'wind deflector']],
    ['radiator guard', ['radiator guard', 'radiator cover', 'radiator protector']],
    ['fuel tuner',     ['fuelx', 'fuel x', 'autotune', 'fuel tuner', 'fuel controller', 'fuel module', 'ecu flash', 'piggyback ecu', 'race dynamics', 'bazzaz', 'powertronic', 'traction control module', 'quickshifter']],
    ['led pod',        ['led pod', 'led pods', 'compact led', 'pod lamp', 'spot light', 'spotlight', 'auxiliary led']],
    ['crash guard',    ['crash guard', 'crashguard', 'crash-guard', 'knuckle guard', 'thanos guard']],
    ['engine guard',   ['engine guard', 'sump guard', 'belly pan']],
    ['saree guard',    ['saree guard', 'leg guard']],
    ['frame slider',   ['frame slider', 'engine slider']],
    ['spark plug',     ['spark plug', 'iridium plug', 'ngk plug', 'bosch plug']],
    ['air filter',     ['air filter', 'air cleaner', 'performance filter']],
    ['oil filter',     ['oil filter']],
    ['engine oil',     ['engine oil', 'motor oil', '10w30', '10w40', '20w40', '15w50']],
    ['brake pad',      ['brake pad', 'disc pad', 'sintered brake', 'brakepads']],
    ['brake disc',     ['brake disc', 'brake rotor', 'disc rotor']],
    ['chain',          ['drive chain', 'o-ring chain', 'chain kit', 'chain set']],
    ['sprocket kit',   ['sprocket kit', 'sprocket set', 'chain sprocket']],
    ['grip set',       ['grip set', 'handlebar grip', 'bar grip']],
    ['bar end',        ['bar end', 'bar-end', 'handlebar end', 'handlebar weight']],
    ['mirror',         ['rear view mirror', 'side mirror', 'wing mirror', 'rvm']],
    ['headlight',      ['headlight', 'head lamp', 'projector headlight', 'led headlight']],
    ['indicator',      ['turn signal', 'blinker', 'indicator light', 'led indicator', 'led turn']],
    ['tail light',     ['tail light', 'tail lamp', 'brake light', 'led tail']],
    ['seat cover',     ['seat cover', 'seat protector']],
    ['tank pad',       ['tank pad', 'tank protector', 'fuel tank cover', 'tank grip']],
    ['tyre',           ['tyre', 'tire']],
    ['tube',           ['inner tube', 'tube tyre']],
    ['battery',        ['battery', 'gel battery', 'lead acid']],
  ];

  // Brand-only tags - sharing just a brand is NOT a fitment signal.
  // "Honda CB200X guard" and "Honda CB350 suspension" both have 'honda'
  // but are for completely different bikes. Model tags must match.
  const BRAND_TAGS = new Set([
    'honda', 'royal-enfield', 'bajaj', 'tvs', 'yamaha', 'suzuki',
    'kawasaki', 'ktm', 'hero', 'jawa', 'triumph', 'benelli',
    'ducati', 'harley', 'bmw', 'husqvarna', 'cfmoto',
  ]);
  const CATEGORY_PAIRS = [
    ['crash', 'guard'], ['engine', 'guard'],
    ['spark', 'plug'], ['air', 'filter'], ['oil', 'filter'],
    ['brake', 'pad'], ['brake', 'disc'], ['brake', 'shoe'], ['brake', 'fluid'],
    ['chain', 'lube'], ['chain', 'kit'], ['grip', 'set'],
    ['seat', 'cover'], ['tank', 'pad'],
    ['flash', 'module'], ['hazard', 'flash'], ['hazard', 'module'],
    ['fog', 'light'], ['led', 'headlight'], ['fork', 'seal'], ['clutch', 'plate'],
    ['bar', 'end'], ['lever', 'set'], ['barrel', 'exhaust'],
    ['twin', 'exhaust'], ['galactic', 'exhaust'],
    ['face', 'helmet'], // belt+suspenders: catches helmet-vs-helmet even if TERMINAL_TYPES misses
  ];

  // Same-terminal-type: never suggest another one of these
  const TERMINAL_TYPES = new Set([
    'exhaust', 'silencer', 'muffler', 'hazard module', 'flash module',
    'battery', 'tyre', 'tube', 'fog light',
    'helmet',      // never show another helmet for a helmet buyer
    'suspension',  // never show another suspension for a suspension buyer
    'bluetooth intercom', // never show another intercom
  ]);

  // ── BENEFIT COPY - why the rider NEEDS this upsell ───────────────────────
  // Keyed by the UPSELL product's inferred type.
  // Shown as a bold pitch line under the product name in the widget.
  const BENEFIT_COPY = {
    // Helmet ecosystem
    'bluetooth intercom': '📞 Add calls, music & GPS - hands-free, head-up',
    'intercom':           '📞 Rider-to-rider comms up to 1km',
    'visor':              '👁️ Crystal optics - no glare, no distortion',
    'helmet':             '🪖 Full head protection - ECE certified',
    'helmet bag':         '🎒 Keeps your ₹6,000 helmet scratch-free',
    // Ignition & service
    'spark plug':         '⚡ Faster ignition & up to 20% better fuel burn',
    'iridium plug':       '⚡ Precision ignition - sharper throttle response',
    'air filter':         '💨 +15–20% airflow - more pull from every gear',
    'engine oil':         '🛡️ Engine protection at every RPM, every ride',
    'oil filter':         '🔧 Cleaner oil = longer engine life',
    'gear oil':           '⚙️ Smoother gearbox shifts, reduced wear',
    'brake fluid':        '🛑 Fresh fluid prevents brake fade under hard braking',
    'fork oil':           '🏍️ Restored suspension feel - sharper cornering',
    // Brakes
    'brake pad':          '🛑 Sharper bite - shorter stopping distance',
    'brake disc':         '🛑 New pads need clean rotors to work as designed',
    'brake shoe':         '🛑 Complete the drum brake service',
    'brake caliper':      '🛑 Full caliper refresh - consistent braking force',
    // Drivetrain
    'chain':              '⚙️ Smooth power transfer, zero slack',
    'sprocket kit':       '⚙️ Better acceleration response from every gear',
    'chain lube':         '⚙️ Extends chain life by up to 3×',
    'chain cleaner':      '🔧 Clean chain = better lube penetration',
    'clutch plate':       '⚙️ Crisp clutch engagement, no slip',
    'clutch cable':       '🔧 Light lever feel - eliminate cable stretch',
    'brake cable':        '🔧 Consistent brake lever response',
    // Tyres
    'tyre':               '🔄 Better contact patch - more grip in corners',
    'tube':               '🔧 Complete the tyre fitment properly',
    // Lighting
    'fog light':          '💡 2× road visibility - ride nights fearlessly',
    'auxiliary light':    '💡 Illuminate hazards before they reach you',
    'led pod':            '💡 4800+ lumens - cut through darkness',
    'led headlight':      '💡 3× brighter than stock - zero blind spots',
    'headlight':          '💡 Daylight-bright beam - seen and see further',
    'indicator':          '👁️ Brighter turn signals - seen in direct sunlight',
    'tail light':         '👁️ High-vis brake light - cars see you sooner',
    'led tail light':     '👁️ Instant-on LED - rear visibility upgrade',
    'led kit':            '💡 Full LED conversion - front to rear',
    // Hazard & electrical
    'hazard module':      '⚡ 60 flash patterns - hazard + integrated tail sync',
    'flash module':       '⚡ Smarter blinkers - turn signal meets hazard mode',
    'switch kit':         '🔌 Plug & play control - no wiring mess, IP-67 sealed',
    'dimmer':             '🔆 4-level brightness control - city to highway in one click',
    'wiring harness':     '🔌 Clean relay-protected install - lights power on right',
    'battery':            '⚡ Reliable cold starts - every morning, every season',
    // Guards & protection
    'crash guard':        '🛡️ Protects engine and frame in a lowside',
    'engine guard':       '🛡️ Guards your sump - cheap insurance on any terrain',
    'frame slider':       '🛡️ Limits damage in a slide - saves your fairings',
    'saree guard':        '🛡️ Prevents fabric from hitting the chain',
    'sump guard':         '🛡️ Belly protection on trails and rough roads',
    'tank guard':         '🛡️ Shields your tank from bag buckles & knees',
    'tank pad':           '🛡️ No more knee scratches on your paint',
    'seat cover':         '🏍️ Grip surface + comfort on long hauls',
    // Ergonomics
    'handlebar':          '🏍️ Better leverage - precise steering input',
    'grip set':           '🤝 Better grip = better control, always',
    'bar end':            '🏍️ Kills handlebar buzz - less fatigue on long rides',
    'mirror':             '👁️ Full rear visibility - wider field, less blind spot',
    'lever set':          '✋ Adjustable reach - fits your hand, not the other way',
    // Suspension
    'suspension':         '🏍️ Already upgrading suspension? Complete the build',
    'fork':               '🔧 Fresh fork oil completes the suspension service',
    // Phone & mounts
    'phone holder':       '📱 Lock your phone in - navigation & calls on the go',
    'phone mount':        '📱 Secure mount - no rattles, no drops',
    // Windscreen
    'windscreen':         '💨 Cuts wind blast - less fatigue on highway runs',
    'windshield':         '💨 Reduces turbulence - cleaner ride at speed',
    // Radiator
    'radiator guard':     '🛡️ Protects your radiator from debris & stones',
    // Footpegs
    'fuel tuner':         '🔥 Full tune needs full bolt-ons - exhaust + filter first',
    'ecu':                '🔥 ECU tune unlocks full gains only with free-flow exhaust',
    'rearset':            '🦶 Adjustable position - your riding, your ergonomics',
    // Exhaust
    'exhaust':            '🔊 Better flow, stronger pull through the rev range',
    'silencer':           '🔊 Tuned note - sounds right, performs better',
    'muffler':            '🔊 Improved exhaust scavenging - more power out back',
  };

  function getBenefit(upsellProduct) {
    const pt = inferTypeFromTitle(upsellProduct.title) || lc(upsellProduct.product_type);
    return BENEFIT_COPY[pt] || null;
  }

  const HEADINGS = [
    'Make It a Full Kit', 'Complete the Build', 'Build the Full Setup',
    'Unleash the Full Kit', 'Frequently Bought Together',
  ];
  const SOCIAL_COPY = [
    'enthusiasts built this combo this week', 'enthusiasts upgraded with this',
    'enthusiasts who bought this also got it', 'enthusiasts went full kit',
  ];

  // ── TRIPLE REWARD ───────────────────────────────────────────────────────────
  // Store runs "Buy 3 items, get 3% off" (auto-applied at checkout). Surface it
  // in the widget to push the rider to the full 3-item kit. Tune to match the
  // live cart promo.
  const REWARD = { minItems: 3, pct: 3, label: 'Triple Reward' };

  const fmt = (c) => '₹' + Math.round(c / 100).toLocaleString('en-IN');
  // Like fmt but keeps paise when the value isn't a whole rupee (e.g. a 3% cut → ₹3,243.68)
  const money = (c) => {
    const r = c / 100;
    return '₹' + r.toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 });
  };
  const pick = (a) => a[Math.floor(Math.random() * a.length)];
  const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
  const lc = (s) => (s || '').toLowerCase().trim();

  function inferTypeFromTitle(title) {
    const t = lc(title);
    for (const [type, kws] of TITLE_TYPES) {
      if (kws.some(k => t.includes(k))) return type;
    }
    return '';
  }

  function sigWords(s) {
    return lc(s).split(/[\W_]+/).filter(w => w.length > 3 && !/^\d+$/.test(w));
  }

  // Single-word terminal signals - if BOTH titles share this word, it's same category.
  // Catches "AXOR X-Cross Helmet" vs "SMK Retro Seven Helmet" without needing 'face'.
  const TERMINAL_WORDS = new Set(['helmet', 'exhaust', 'silencer', 'muffler', 'battery', 'tyre', 'suspension', 'sprocket']);

  function isSameProductFamily(titleA, titleB, typeA, typeB) {
    // Gate A: same terminal type (exhaust vs exhaust, helmet vs helmet)
    if (typeA && typeB && typeA === typeB && TERMINAL_TYPES.has(typeA)) return true;

    const wa = sigWords(titleA), wb = sigWords(titleB);
    if (!wa.length || !wb.length) return false;

    // Gate D: single terminal word shared - any two helmets/exhausts/etc.
    // Only when the two kinds of part are the same or one is unknown: a
    // "Helmet Bluetooth Intercom" shares the word "helmet" with every helmet on
    // the store and is the single best thing to sell alongside one.
    if ((!typeA || !typeB || typeA === typeB) &&
        wa.some(w => TERMINAL_WORDS.has(w) && wb.includes(w))) return true;

    // v2.0: when both kinds of part are known and they differ, that settles it.
    // The similarity gates below compare title WORDS, and two different parts
    // for the same car share a long vehicle list - "Brembo Xtra Front Brake
    // Pads for Skoda & VW (RAPID/FABIA...)" scored 0.6 similar to the matching
    // DISC and was being thrown away as the same product.
    if (typeA && typeB && typeA !== typeB) return false;

    const common = wa.filter(w => wb.includes(w));
    const sim = common.length / Math.max(wa.length, wb.length);

    // Gate B: high similarity
    if (sim >= 0.60) return true;

    // Gate C: moderate similarity + shared category phrase pair
    if (sim >= 0.30) {
      const sharedPhrase = CATEGORY_PAIRS.some(([w1, w2]) =>
        wa.includes(w1) && wa.includes(w2) && wb.includes(w1) && wb.includes(w2)
      );
      if (sharedPhrase) return true;
    }
    return false;
  }

  function getComplements(type) {
    const t = lc(type);
    if (!t) return [];
    if (COMPLEMENT[t]) return COMPLEMENT[t];
    for (const [k, v] of Object.entries(COMPLEMENT)) {
      if (t.includes(k) || k.includes(t)) return v;
    }
    return [];
  }

  // ── CAR AWARENESS ───────────────────────────────────────────────────────────
  // v2.0: cars are no longer restricted to "universal" fluids. The catalogue
  // DOES carry car fitment tags - two styles of them:
  //   Brembo/EBC style : "SKODA RAPID/FABIA after 2012"  (make + models + years)
  //   BMC style        : "Polo", "Vento", "Rapid", "1.2 TSI"
  // fitmentTagsOf() reads both, so a car page can now cross-sell the disc, the
  // filter and the plug for THAT car instead of a generic bottle of additive.
  // These two lists are only the last-resort complements for car products whose
  // type has no entry in COMPLEMENT at all (body kits, armrests, gear knobs).
  const CAR_UNIVERSAL_COMPLEMENTS = [
    'engine oil', 'coolant', 'additive', 'fuel optimizer',
    'engine treatment', 'brake fluid', 'car care', 'cleaner',
  ];
  // Type/title hints that mark a product as car-only (used when the
  // 'vehicle-car' tag is absent).
  const CAR_HINT_RE = /\bcar\b|body kit|bumper|spoiler|diffuser|side skirt|window louver|armrest|gear knob|steering wheel|paddle shifter|cold air intake/;
  const BIKE_HINT_RE = /motorcycle|scooter|\bbike\b|helmet/;

  function isCarProduct(data) {
    const tags = (Array.isArray(data.productTags) ? data.productTags : []).map(lc);
    if (tags.includes('vehicle-car')) return true;
    if (tags.includes('vehicle-bike')) return false;
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '');
    return CAR_HINT_RE.test(t) && !BIKE_HINT_RE.test(t);
  }

  // 'car' | 'bike' | '' - used to hard-block cross-vehicle upsells
  // (e.g. a JASO-MA2 motorcycle oil must never show on a car cold-air-intake).
  // Makes that only ever mean one kind of vehicle. Honda, Suzuki, BMW and
  // Mahindra are deliberately absent: they sell both.
  const CAR_ONLY_MAKES = new Set([
    'audi', 'mercedes', 'mercedes-benz', 'skoda', 'volkswagen', 'vw', 'porsche',
    'volvo', 'jaguar', 'landrover', 'land rover', 'maserati', 'rolls', 'bentley',
    'aston', 'aston martin', 'lamborghini', 'ferrari', 'mclaren', 'bugatti',
    'alfa', 'seat', 'peugeot', 'opel', 'subaru', 'infiniti', 'cadillac',
    'chrysler', 'dodge', 'lincoln', 'acura', 'genesis', 'daihatsu', 'ssangyong',
    'tesla', 'byd', 'hyundai', 'kia', 'nissan', 'renault', 'mitsubishi', 'ford',
    'chevrolet', 'citroen', 'fiat', 'isuzu', 'jeep', 'lexus', 'maruti',
    'maruti suzuki', 'maruti/suzuki', 'tata', 'toyota', 'datsun', 'rover',
  ]);
  const BIKE_ONLY_MAKES = new Set([
    'royal-enfield', 'royal enfield', 'bajaj', 'tvs', 'yamaha', 'kawasaki',
    'ktm', 'hero', 'jawa', 'benelli', 'ducati', 'harley', 'husqvarna',
    'cfmoto', 'aprilia', 'vespa', 'piaggio', 'moto guzzi', 'keeway', 'zontes',
    'gasgas', 'sherco', 'ather', 'revolt', 'ultraviolette', 'okinawa',
    'ampere', 'vida', 'norton', 'yezdi', 'triumph', 'motorrad',
  ]);

  function vehicleClassOf(tags, title) {
    const tg = (Array.isArray(tags) ? tags : String(tags || '').split(', ')).map(lc);
    const isCar = tg.includes('vehicle-car'), isBike = tg.includes('vehicle-bike');
    // Both tags means it genuinely fits both - "Motul 300V Competition for Track
    // & Racing Cars / Track Motorcycles" - so it belongs in both collections and
    // must not be blocked from either kind of page.
    if (isCar && isBike) return '';
    if (isCar)  return 'car';
    if (isBike) return 'bike';

    // Not every product carries the class tag - the DBA and Pagid brake SKUs
    // from the 7-brand expansion often have nothing but a bare make - so the
    // make decides. Without this a BMW X3 pad page could offer a Pulsar plug.
    for (let i = 0; i < tg.length; i++) {
      if (CAR_ONLY_MAKES.has(tg[i]))  return 'car';
      if (BIKE_ONLY_MAKES.has(tg[i])) return 'bike';
    }

    const t = lc(title);
    if (CAR_HINT_RE.test(t) && !BIKE_HINT_RE.test(t)) return 'car';
    if (BIKE_HINT_RE.test(t)) return 'bike';
    return '';
  }

  // Performance/tuning buyer? (cold air intake, exhaust, ECU, intake, tuner…)
  const PERF_RE = /cold air intake|\bintake\b|induction|performance|exhaust|\becu\b|tuner|fuel optimizer|sprocket|suspension/;
  function isPerformance(data) {
    const tags = (Array.isArray(data.productTags) ? data.productTags : []).map(lc).join(' ');
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '') + ' ' + tags;
    return PERF_RE.test(t);
  }
  const CAR_PERF_COMPLEMENTS = [
    'fuel optimizer', 'additive', 'engine treatment', 'coolant', 'car care', 'cleaner',
  ];

  // Effective complement list. The COMPLEMENT map is the first answer for every
  // product, car or bike; the universal lists only fill in when the map has
  // nothing for this type.
  function getComplementsFor(data) {
    const curType = lc(data.productType) || inferTypeFromTitle(data.productTitle || '');
    const mapped = getComplements(curType);
    if (mapped.length) return mapped;
    if (isCarProduct(data)) {
      return isPerformance(data) ? CAR_PERF_COMPLEMENTS : CAR_UNIVERSAL_COMPLEMENTS;
    }
    return [];
  }

  // ── COLLECTION PUSH ─────────────────────────────────────────────────────────
  // Map a product to the most relevant collection to surface as a
  // "Explore more in …" CTA. Handles verified live on the store.
  const LIGHT_RE     = /fog|headlight|tail light|indicator|\bled\b|switch kit|wiring|hazard|lamp|lighting/;
  const PROTECT_RE   = /guard|slider|bash plate|protector|radiator|sump/;
  const TOURING_RE   = /luggage|top box|side box|top case|carrier|backrest|saddle|phone mount|gps|navigation|paddock|stand|mount/;
  const HELMET_RE    = /helmet|visor|intercom|headset|riding jacket|hydration/;
  function collectionFor(data) {
    const klass = vehicleClassOf(data.productTags, data.productTitle);
    const t = lc(data.productType) + ' ' + lc(data.productTitle || '');
    if (klass === 'car' || isCarProduct(data)) {
      // Car air filter / intake itself → broader performance category (no self-loop).
      if (/air filter|intake/.test(t)) return { handle: 'car-performance-upgrades', label: 'Car Performance Upgrades' };
      if (isPerformance(data)) return { handle: 'car-performance-upgrades', label: 'Car Performance Upgrades' };
      // Ignition/plug: the #1 complement (air filter) is model-specific with many
      // options - so point the rider to pick their fit instead of guessing one.
      if (/spark plug|\bplug\b|ignition/.test(t)) return { handle: 'car-air-filters-intakes', label: 'performance air filter', cta: 'Find your' };
      return { handle: 'shop-by-car-premium-automotive-accessories', label: 'Shop by Car' };
    }
    // Bike
    if (HELMET_RE.test(t))   return { handle: 'rider-essentials', label: 'Rider Essentials' };
    if (LIGHT_RE.test(t))    return { handle: 'lighting-visibility', label: 'Lighting & Visibility' };
    if (PROTECT_RE.test(t))  return { handle: 'bike-protection', label: 'Protection & Guards' };
    if (TOURING_RE.test(t))  return { handle: 'touring-mounts', label: 'Touring & Mounts' };
    return { handle: 'performance-parts', label: 'Performance Parts' };
  }

  function getSelectedVariant() {
    return (
      new URLSearchParams(window.location.search).get('variant') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value ||
      document.querySelector('form[action*="/cart/add"] select[name="id"]')?.value ||
      String(window.RTUpsellData?.productFirstVariantId || '')
    );
  }

  const suggestToCents = (v) =>
    Math.round(parseFloat(String(v || 0).replace(/,/g, '')) * 100);

  // /products/{handle}.js returns prices already in paise (integers). Keep them
  // as-is; only convert if a rupee-looking value slips through. Shared by the
  // L0 manual-upsell path and fetchHandlePicks so both agree.
  const toPaise = (val) =>
    (val > 1000 && Number.isInteger(val)) ? val : Math.round(parseFloat(val) * 100);

  // HTML-escape for values interpolated into innerHTML from live product JSON.
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  function normSuggest(p) {
    return {
      id: p.id, handle: p.handle || '', title: p.title || '', vendor: p.vendor || '',
      product_type: p.type || p.product_type || '',
      featured_image: p.featured_image?.url ? { src: p.featured_image.url } : null,
      tags: Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(', '),
      variants: [{
        id: p.variants?.[0]?.id || p.id,
        price: suggestToCents(p.price),
        compare_at_price: p.compare_at_price_max
          ? suggestToCents(p.compare_at_price_max) : null,
      }],
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  v2.0 RANKING ENGINE
  //
  //  Ported from the cart-page rail ("Complete your build"), which was built
  //  and measured against live order data. See rt-bundle-upsell-spec.md.
  //
  //  The one idea that matters: "related" means SIMILAR, and a similar product
  //  is a SUBSTITUTE. Showing seven more sets of front pads to someone looking
  //  at front pads competes with the sale instead of growing it. What they need
  //  is a DIFFERENT part for the SAME vehicle - the disc, the fluid, the filter,
  //  the plug. Everything below exists to find that.
  // ══════════════════════════════════════════════════════════════════════════

  // ── FITMENT TAGS ──────────────────────────────────────────────────────────
  // Which tags actually say what a product fits. Everything else - UI badges,
  // class flags, category words, marketing words - is noise that would
  // otherwise score every product against every other product.
  //
  // Two tag styles live in this catalogue and BOTH have to work:
  //   "SKODA RAPID/FABIA after 2012", "Duke 390", "fits-CB 350"   (specific)
  //   "Polo", "Vento", "Baleno", "Swift"                          (bare model)
  // The old digit test only saw the first kind, which is why car pages fell
  // back to generic fluids.
  const TAG_JUNK_PREFIX = ['vehicle-', 'pill-', 'rider:', 'rider_', 'collection-', 'badge-', 'free-'];
  const TAG_JUNK = new Set([
    // marketing / merchandising
    'new', 'sale', 'best-seller', 'bestseller', 'featured', 'luxury', 'premium',
    'performance', 'performance-parts', 'car-accessories', 'utilities', 'combo',
    'pre-order', 'preorder', 'universal', 'oem', 'genuine', 'imported', 'accessories',
    'car', 'bike', 'utility', 'protection', 'body-kit', 'body kit',
    // axle / position (read separately by axleOf)
    'front', 'rear', 'front brake pad', 'rear brake pad',
    // category + product-line words that would masquerade as fitment
    'brake pads', 'brake pad', 'brake discs', 'brake disc', 'oe brake pads',
    'xtra performance pads', 'yellowstuff performance pads', 'performance disc',
    'ultimax oe-replacement pads', 'drilled performance disc', 'ceramic brake pad',
    'sintered brake pad', 'whisker', 'brake shoe', 'brake fluid',
    'air filter', 'car air filter', 'performance air filter', 'reusable filter',
    'washable air filter', 'pod filter', 'oil filter', 'engine oil',
    'car engine oil', 'gear oil', 'additive', 'fuel additive', 'coolant',
    'spark plug', 'car spark plug', 'iridium spark plug', 'laser iridium',
    'iridium ix', 'ignition', 'helmets', 'helmet', 'exhaust', 'silencer',
    'suspension', 'chain', 'sprocket', 'tyre', 'battery', 'mirror',
    'cold air intake', 'performance intake', 'lighting', 'touring',
    // riding style / segment words, not models
    'street', 'superbike', 'superbikes', 'racing', 'race', 'track', 'sport',
    'sports', 'cruiser', 'commuter', 'offroad', 'off road', 'adventure',
    'bikes', 'motorcycle', 'motorcycles', 'scooter', 'scooters', 'touring bike',
  ]);

  // Sharing only a MAKE is not fitment ("Honda CB350 pads" vs "Honda Civic
  // filter"). Scored, but at a fraction of a model match.
  const CAR_BRAND_TAGS = new Set([
    'audi', 'bmw', 'chevrolet', 'citroen', 'datsun', 'fiat', 'ford', 'honda',
    'hyundai', 'isuzu', 'jaguar', 'jeep', 'kia', 'landrover', 'land rover',
    'maserati', 'maruti', 'maruti suzuki', 'maruti/suzuki', 'mercedes',
    'mercedes-benz', 'mg', 'mini', 'mitsubishi', 'mitsubush', 'nissan',
    'porsche', 'renault', 'rolls', 'skoda', 'suzuki', 'tata', 'toyota',
    'volvo', 'volkswagen', 'vw', 'lexus', 'mahindra',
    // the luxury end of the brake catalogue, and the rest of what could turn up
    'bentley', 'aston', 'aston martin', 'lamborghini', 'ferrari', 'mclaren',
    'rolls royce', 'bugatti', 'alfa', 'alfa romeo', 'seat', 'peugeot', 'opel',
    'subaru', 'infiniti', 'cadillac', 'chrysler', 'dodge', 'lincoln', 'acura',
    'genesis', 'daihatsu', 'force', 'premier', 'ashok', 'eicher',
    'ssangyong', 'tesla', 'byd', 'hummer', 'morris', 'proton', 'perodua',
    'rover', 'range rover',   // "Range Rover Sport" carries no other make word
    // two-wheeler makes not already in BRAND_TAGS
    'aprilia', 'vespa', 'piaggio', 'moto guzzi', 'keeway', 'zontes', 'gasgas',
    'sherco', 'ather', 'revolt', 'ultraviolette', 'okinawa', 'ampere', 'vida',
    'norton', 'indian', 'motorrad', 'yezdi', 'bgauss', 'tork', 'oben', 'river',
    'matter', 'komaki', 'simple', 'lml', 'mahindra',
  ]);

  // The part maker is not the vehicle. Nearly every product is tagged with its
  // own brand, so without this "Brembo" reads as fitment and every Brembo part
  // looks like it fits every other Brembo part's car.
  const PART_BRAND_TAGS = new Set([
    'brembo', 'ebc', 'bmc', 'ngk', 'ngage', 'motul', 'liqui moly', 'pagid',
    'dba', 'hel', 'k&n', 'red rooster', '66bhp', 'axor', 'smk', 'studds',
    'barrel', 'rolon', 'silver stallion', 'crank1', 'mototorque', 'idemitsu',
    'race dynamics', 'dieseltronic', 'motocare', 'legundary', 'esperer',
    'maddog', 'maddog industries', 'hjc', 'ls2', 'vega', 'steelbird', 'mt',
    'brakekraft', 'maxima', 'gulf', 'elf', 'apollo', 'ceat', 'mrf',
    'rawtorque', 'brakekraft', 'bosch', 'mahle', 'denso', 'castrol', 'shell',
  ]);

  // Part numbers: "P85121X", "FB01180", "FB576/08". Deliberately requires FOUR
  // digits (or a slash) so real model tags - cb350rs, duke 390, rc 200 - survive.
  const PART_NUMBER_RE = /^[a-z]{1,4}[\s-]?\d{4,}[a-z0-9-]*$|^[a-z]{1,4}\d{2,}\/\d+$/;

  // Every category word the type-inference table knows about. A tag that
  // CONTAINS one of them describes the part, not the vehicle: "car fog lights",
  // "LED fog lights", "car body kit", "brake pads", "interceptor650-exhaust".
  // Built from the same table, so extending TITLE_TYPES extends this too.
  const CATEGORY_WORDS = (function () {
    const out = [];
    TITLE_TYPES.forEach(function (pair) {
      out.push(pair[0]);
      pair[1].forEach(function (k) { if (k.length >= 4) out.push(k.trim()); });
    });
    return out;
  })();

  // Spec sheets and merchandising dressed up as tags: "18 Month Warranty",
  // "9600 Lumens", "40W", "OEM Fit", "Plug & Play".
  const TAG_NOISE_RE =
    /\bwarranty\b|\blumens?\b|\bwatts?\b|\bmonths?\b|plug.?(and|&|n).?play|\bmade in\b|^oem\b|^\d+\s*(w|lm|mm|ml|kg|v|amp)$/;

  // Part words the type table does not carry as a whole phrase. A tag with one
  // of these in it is describing the part ("Car Fog Lights", "LED Fog Lights",
  // "Car Body Kit"), never the vehicle - no model in this catalogue is called
  // "light" or "cover".
  const PART_WORD_RE = /\b(lights?|lamps?|kits?|covers?|mats?|decals?|stickers?|horns?|wipers?|armrest|knobs?|pedals?|spoilers?|bumpers?|grille|visors?|guards?|filters?|pads?|discs?|plugs?|fluid|jackets?|gloves?|stands?|sliders?|accessor(y|ies)|protection|protector|steel|aluminium|aluminum|carbon|combo|premium|reusable|washable|synthetic|speakers?|range|beam|display|camera|screen|touchscreen|chargers?|harness|switch|dimmer|waterproof)\b/;

  // Does this text name a vehicle: a make, or a model code like F55, L460,
  // X166, GT4, R56, W205? Used to rescue tags that mention a part as well.
  function namesVehicleText(t) {
    return MAKE_RE.test(t) || /\b[a-z]{1,4}\d{1,3}[a-z]?\b/.test(t);
  }

  function fitmentTag(raw, vendor) {
    let t = lc(raw);
    if (!t) return null;
    if (t.indexOf('fits-') === 0) t = t.slice(5).trim();   // "fits-CB 350" -> "cb 350"
    // Stage tags name the bike and then the kit: "duke390-stage1",
    // "KTM Adventure 250-stage1". Keep the bike, drop the kit. (Throwing the
    // whole tag away left the PowerTRONIC ECU with no model at all, so every
    // KTM part scored as a different vehicle and the widget came up empty.)
    t = t.replace(/[-\s]stage\s*\d.*$/, '').trim();
    if (!t) return null;
    if (t.length < 3 && !/^[a-z]\d$/.test(t)) return null;   // "R3" and "R7" are bikes
    for (let i = 0; i < TAG_JUNK_PREFIX.length; i++) {
      if (t.indexOf(TAG_JUNK_PREFIX[i]) === 0) return null;
    }
    if (TAG_JUNK.has(t)) return null;
    if (PART_BRAND_TAGS.has(t)) return null;
    if (PART_BRAND_TAGS.has(t.split(/[\s/]/)[0])) return null;   // "Axor Helmets", "Brembo Xtra"
    if (vendor) {                                          // its own brand tag
      const v = lc(vendor);
      if (t === v || t === v.split(/[\s/]/)[0] || v.indexOf(t + ' ') === 0) return null;
    }
    if (PART_NUMBER_RE.test(t)) return null;
    if (TAG_NOISE_RE.test(t)) return null;
    // A tag can name the part AND the car: "Mini Cooper F55/F56 (2013 -) Petrol
    // JCW kit", "MERCEDES GLE 43 AMG with 375 drilled disc", "Land Rover RANGE
    // ROVER L460" - and "range" is also how an auxiliary light's beam is spec'd.
    // Keep the tag when a make or a model code is in it.
    if (PART_WORD_RE.test(t) && !namesVehicleText(t)) return null;
    for (let j = 0; j < CATEGORY_WORDS.length; j++) {
      if (t.indexOf(CATEGORY_WORDS[j]) !== -1) return null;
    }
    return t;
  }

  function isBrandTag(t) {
    if (CAR_BRAND_TAGS.has(t) || BRAND_TAGS.has(t) || BRAND_TAGS.has(t.replace(/\s+/g, '-'))) return true;
    // "Honda Motorcycle", "Maruti Suzuki Cars" - a make with a class word after it
    const m = t.match(/^(.+?)\s+(motorcycle|motorcycles|bike|bikes|car|cars|vehicles)$/);
    return !!(m && (CAR_BRAND_TAGS.has(m[1]) || BRAND_TAGS.has(m[1].replace(/\s+/g, '-'))));
  }

  // -> { model: [...], brand: [...] }
  function fitmentTagsOf(tags, vendor) {
    const list = Array.isArray(tags) ? tags : String(tags || '').split(', ');
    const out = { model: [], brand: [] };
    list.forEach(raw => {
      const t = fitmentTag(raw, vendor);
      if (!t) return;
      if (isBrandTag(t)) { if (out.brand.indexOf(t) === -1) out.brand.push(t); }
      else if (out.model.indexOf(t) === -1) out.model.push(t);
    });
    return out;
  }

  // One name for a kind of part, whichever field it came from. product_type is
  // raw and inconsistent ("Brake Pads" on one product, "Iridium Plug" on
  // another) while inferTypeFromTitle is normalised, so the title wins and the
  // type field is put through the same normaliser. Without this "brake pads"
  // and "brake pad" read as different categories and the -45 substitute
  // penalty never fires.
  function typeOf(title, rawType) {
    const t = lc(title || '');

    // Helmets vs helmet accessories, which the keyword table cannot separate
    // because every one of these titles contains the word "helmet". The product
    // is whichever noun comes LAST: "Dual Visor Speedometer Helmet" is a
    // helmet, "Replacement Helmet Shield" is a visor. Communication gear is
    // excluded outright - it is the best thing to sell WITH a helmet.
    if (/\bhelmet/.test(t) &&
        !/(intercom|headset|speaker|bluetooth|bag|lock|holder|care|cleaner|liner|pinlock)/.test(t)) {
      // Only the head of the title names the product; everything after the
      // first comma or bracket is colour and certification blurb, and half the
      // helmets on this store say "(Dual Visor, ISI & DOT Certified)" there.
      const head = t.split(/[,(|]/)[0];
      const hi = head.lastIndexOf('helmet');
      const vi = Math.max(head.lastIndexOf('visor'), head.lastIndexOf('shield'));
      if (hi > vi) return 'helmet';
    }

    return inferTypeFromTitle(t) ||
           inferTypeFromTitle(lc(rawType)) ||
           lc(rawType);
  }

  // Does this tag set actually name a vehicle? Either a make (ktm, volkswagen)
  // or a model tag carrying a displacement or a year ("duke 390", "himalayan
  // 450", "cb350rs", "skoda rapid/fabia after 2012").
  //
  // Universal gear is tagged with its spec sheet - "600m range", "50mm
  // speakers", "IPX6" - and those must NOT read as a vehicle, or a Bluetooth
  // intercom looks model-specific and gets held back from the helmet it was
  // made for.
  // Some products name the vehicle only in the title ("BMC FM569/08 Air Filter
  // for BMW Motorrad K1300 R" carries no make tag at all), so the title is
  // checked too. "mini" is left out - too many ordinary words contain it.
  const MAKE_RE = (function () {
    const rx = (m) => m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const ambiguous = { mini: 1, mg: 1, mt: 1 };   // ordinary words, not makes
    const makes = [];
    CAR_BRAND_TAGS.forEach(m => { if (!ambiguous[m]) makes.push(rx(m)); });
    BRAND_TAGS.forEach(m => { if (!ambiguous[m]) makes.push(rx(m).replace(/-/g, '[- ]')); });
    return new RegExp('\\b(' + makes.join('|') + ')\\b');
  })();

  function namesAVehicle(ft, title) {
    if (ft.brand.length > 0) return true;
    if (ft.model.some(t => /\d{3}/.test(t))) return true;   // "duke 390", "458/599", "w212"
    return !!title && MAKE_RE.test(lc(title));
  }

  // The words inside a fitment tag that actually identify the vehicle.
  function tagWords(t) {
    const out = [];
    t.split(/[^a-z0-9.]+/).forEach(w => {
      if (w.length < 3 || MODEL_STOPWORDS.has(w)) return;
      if (/^\d+$/.test(w) && w.length > 3) return;          // years
      // A make and a part name are shared by half the catalogue: "MERCEDES GLE
      // 43 AMG with 375 drilled disc" and "MERCEDES W124 ... ventilated disc"
      // have "mercedes" and "disc" in common and are thirty years apart.
      if (isBrandTag(w) || PART_WORD_RE.test(w)) return;
      if (CATEGORY_WORDS.indexOf(w) !== -1) return;
      if (out.indexOf(w) === -1) out.push(w);                // "polo/polo gt" is one polo
    });
    return out;
  }

  // Do two fitment tags describe the same vehicle?
  //
  // Containment covers "cb350" against "cb350rs" and "polo" against "vw
  // polo/polo gt/vento after 2012". It does NOT cover the same car written two
  // ways, and this catalogue does that constantly: the Brembo discs are tagged
  // "Skoda RAPID/FABIA (TYPE 2)" while the pads for the same car are tagged
  // "SKODA RAPID/FABIA after 2012", so a disc page could not find its own pads.
  //
  // So fall back to shared model WORDS, with the bar at two. One is not enough:
  // "Duke 390" and "Duke 125" share a word and fit different bikes, and
  // "POLO/POLO GT/VENTO (type 2)" shares only "polo" with "POLO GTI 1.8 310mm",
  // which is a different car with different discs.
  // The years a tag covers, as a range. "after 2012" is open-ended upward,
  // "from 2009 to 2011" is closed, "all models" says nothing.
  function yearRange(t) {
    const ys = (t.match(/\b(?:19|20)\d{2}\b/g) || []).map(Number);
    if (!ys.length) return null;
    let lo = Math.min.apply(null, ys), hi = Math.max.apply(null, ys);
    if (/after|onward|onwards|\+|>|\bfrom\b(?!.*\bto\b)/.test(t)) hi = 9999;
    if (/upto|up to|till|before|older|old model/.test(t)) lo = 0;
    return [lo, hi];
  }

  function tagsMatch(x, y) {
    if (x === y) return true;
    if (x.length >= 4 && y.indexOf(x) !== -1) return true;
    if (y.length >= 4 && x.indexOf(y) !== -1) return true;

    // Same model name, different generation: "RAPID/FABIA after 2012" and
    // "RAPID/FABIA from 2009 to 2011" are two different cars and the store
    // sells different pads for each.
    const rx = yearRange(x), ry = yearRange(y);
    if (rx && ry && (rx[1] < ry[0] || ry[1] < rx[0])) return false;

    // A CLOSED range is one specific old generation ("from 2009 to 2011"), and
    // a tag with no years at all ("RAPID/FABIA (TYPE 2)") gives nothing to
    // check it against. Word overlap alone is not enough to pair those - it
    // would offer the old car's pads to the new car. An open-ended range
    // ("after 2012") is the current car and stays eligible.
    const closed = (r) => r && r[1] < 9999;
    if ((closed(rx) && !ry) || (closed(ry) && !rx)) return false;

    const wx = tagWords(x), wy = tagWords(y);
    let shared = 0;
    for (let i = 0; i < wx.length; i++) if (wy.indexOf(wx[i]) !== -1) shared++;
    return shared >= 2;
  }

  function tagOverlap(a, b) {
    let n = 0;
    a.forEach(x => { if (b.some(y => tagsMatch(x, y))) n++; });
    return n;
  }

  // ── AXLE ──────────────────────────────────────────────────────────────────
  // Tags first: this catalogue tags brake parts "Front"/"Rear" explicitly and
  // many titles carry no axle word at all. A listing covering BOTH ends already
  // sells the pair, so it is neither. \b stops "Rearview Mirror" reading as a
  // rear part.
  function axleOf(tags, title, type) {
    const tg = (Array.isArray(tags) ? tags : String(tags || '').split(', ')).map(lc);
    const tf = tg.indexOf('front') !== -1, tr = tg.indexOf('rear') !== -1;
    if (tf !== tr) return tf ? 'front' : 'rear';
    const s = lc((title || '') + ' ' + (type || ''));
    const f = /\bfront\b/.test(s), r = /\brear\b/.test(s);
    if (f === r) return null;
    return f ? 'front' : 'rear';
  }

  // ── WEIGHTS ───────────────────────────────────────────────────────────────
  // Reasoned, not learned. Anchored to the 50 orders RT1483-RT1532: only 8 held
  // more than one product, 4 of those 8 were a front+rear pair of the same part,
  // a spark plug appears in 5 of 8 and an air filter in 3. There is no click or
  // conversion data behind these numbers - do not present them as optimal.
  const W = {
    sameVehicle:   30,   // came back from a search on the anchor's own model tag
    counterpart:   25,   // the other axle of the same part, same vehicle
    perModelTag:   10,   // per shared model tag (capped)
    modelTagCap:    5,
    perBrandTag:    3,   // per shared make (capped) - weak signal on its own
    brandTagCap:    2,
    curated:       25,   // the merchant put it on this product's own list
    complement:    12,   // type is a known complement of the anchor's type
    discount:       2,
    multiVariant:  -8,   // can't be added blind: needs a size/colour choice
    wrongVehicle: -25,   // both sides name a model and the models don't match
    modelOnUniv:  -20,   // model-specific part against a product that fits anything
    redundant:    -45,   // same kind of part as the anchor, and not the pair
  };

  const ATTACHMENT_BIAS = {
    'spark plug': 15,    // measured: 5 of 8 multi-product orders
    'air filter': 10,    // measured: 3 of 8
    'oil filter':  8,    // inferred from the same servicing habit, not measured
    'engine oil':  5,    // inferred
  };

  // Merchandising preference, deliberately NOT in the table above: there is no
  // order data behind this one. The owner asked for the chain and sprocket kit
  // to be offered alongside performance work (5 Sep 2026). Separate table so
  // the measured numbers stay honest about being measured.
  const PREFERRED = {
    'chain': 15,
    'sprocket kit': 15,
  };

  // A product sold per bike keeps the bike in its VARIANT title, not its tags:
  // the PowerTRONIC ECU is one listing tagged "KTM" with variants "Duke/RC 390
  // (2012-2016)", "Adventure 250 (2020-2023)" and so on. Read the selected one,
  // so switching the variant switches what the bundle offers.
  //
  // Only titles with a number in them are used - "Black", "Medium" and "Default
  // Title" are not vehicles.
  function variantModels() {
    let t = lc(typeof getSelectedVariantTitle === 'function' ? getSelectedVariantTitle() : '');
    if (!t || t.indexOf('default') === 0 || !/\d/.test(t)) return [];
    t = t.split('(')[0].trim();                       // drop the year range
    const m = t.match(/^(.*?)\s*(\d{3,4})\s*$/);      // "duke/rc 390"
    if (!m || !m[1]) return t ? [t] : [];
    return m[1].split('/')                            // -> "duke 390", "rc 390"
      .map(w => (w.trim() + ' ' + m[2]).trim())
      .filter(w => w.length > 3);
  }

  // Everything the ranking needs to know about the product being viewed.
  function buildCtx(data) {
    const title = data.productTitle || '';
    const tags  = Array.isArray(data.productTags) ? data.productTags : [];
    const ft    = fitmentTagsOf(tags, data.productVendor);
    variantModels().forEach(m => { if (ft.model.indexOf(m) === -1) ft.model.push(m); });
    return {
      id:          String(data.productId),
      title:       title,
      type:        typeOf(title, data.productType),
      rawType:     lc(data.productType || ''),
      vendor:      lc(data.productVendor || ''),
      tags:        tags,
      modelTags:   ft.model,
      brandTags:   ft.brand,
      namesVehicle: namesAVehicle(ft, title),
      klass:       vehicleClassOf(tags, title),
      axle:        axleOf(tags, title, data.productType),
      complements: getComplementsFor(data),
    };
  }

  // ── SCORING ───────────────────────────────────────────────────────────────
  function scoreOne(p, ctx) {
    const reasons = [];
    const pt = typeOf(p.title, p.product_type);
    const cand = fitmentTagsOf(p.tags, p.vendor);
    const sharedModelTags = tagOverlap(ctx.modelTags, cand.model);

    // ── The counterpart: the ONE same-category suggestion that is wanted ─────
    // Front + rear of the same part for the same vehicle is this store's most
    // common real co-purchase (4 of the 8 multi-item orders sampled). All four
    // conditions are required - condition 4 is what stops a Jaguar rear pad
    // being offered against Dominar front pads.
    //
    // Worked out BEFORE the same-family check on purpose: the front and rear
    // listings of one part are near-identical strings ("... Front Brake Pad for
    // KTM Duke/ADV/RC" vs "... Rear Brake Pad for KTM Duke/ADV/RC"), so the
    // similarity guard would otherwise throw away the single most valuable
    // suggestion this widget can make.
    const candAxle = axleOf(p.tags, p.title, p.product_type);
    const counterpart =
      !!ctx.axle && !!ctx.type &&                    // 1 anchor is axle-specific
      !!candAxle && candAxle !== ctx.axle &&         // 2 candidate is the other end
      pt === ctx.type &&                             // 3 same kind of part
      sharedModelTags > 0;                           // 4 same vehicle, proven by tags

    // Never a restyle of the same product.
    if (!counterpart && isSameProductFamily(ctx.title, p.title, ctx.type, pt)) {
      return { score: -999, reasons: ['same product family'], sameFamily: true, category: pt };
    }

    // Never across vehicle classes.
    const candClass = vehicleClassOf(p.tags, p.title);
    if (ctx.klass && candClass && ctx.klass !== candClass) {
      return { score: -999, reasons: ['wrong vehicle class'], vehMismatch: true, category: pt };
    }

    let s = 0;

    const sharedModel = sharedModelTags;
    if (sharedModel) {
      s += Math.min(sharedModel, W.modelTagCap) * W.perModelTag;
      reasons.push(sharedModel + ' shared fitment tag' + (sharedModel > 1 ? 's' : ''));
    }
    const sharedBrand = tagOverlap(ctx.brandTags, cand.brand);
    if (sharedBrand) s += Math.min(sharedBrand, W.brandTagCap) * W.perBrandTag;

    // Both sides name a model and none of them match -> wrong vehicle.
    // This is what keeps "rear pads for a Yeti" off a Polo page, and a CB200X
    // guard off a CB350 page.
    // When BOTH sides name a model, the models have to match: "Honda CB200X
    // guard" and "Honda CB350 suspension" share a make and fit nothing in
    // common. When one side only knows its make - the PowerTRONIC ECU is
    // tagged KTM with the models hidden in its variant titles - fall back to
    // the make, or the page can suggest nothing at all.
    const candNamesVehicle = namesAVehicle(cand, p.title);
    let wrongVehicle = false;
    if (ctx.namesVehicle && candNamesVehicle) {
      wrongVehicle = (ctx.modelTags.length && cand.model.length)
        ? sharedModel === 0
        : sharedBrand === 0;
    }
    if (wrongVehicle) { s += W.wrongVehicle; reasons.push('different vehicle'); }

    // Storefront search is fuzzy: a query for "axor helmets" will happily come
    // back with a Pulsar spark plug. Only credit the search when the tags do
    // not contradict it.
    if (p.__sameVehicle && !wrongVehicle) {
      s += W.sameVehicle; reasons.push('fits the same vehicle');
    }

    // The anchor fits anything (a bottle of oil, a helmet) and the candidate
    // fits one bike. Guessing which bike the rider owns is how a KTM plug ends
    // up bundled with universal engine oil.
    if (!ctx.namesVehicle && candNamesVehicle) {
      s += W.modelOnUniv; reasons.push('model-specific');
    }

    if (counterpart) { s += W.counterpart; reasons.push('the other axle of the same part'); }

    // Same kind of part and NOT the pair = a substitute for what they are
    // already looking at. Demoted, not dropped: on a brake-heavy catalogue
    // dropping it outright can leave the widget with nothing.
    // Two Maddog auxiliary lights infer as "led pod" and "led auxiliary
    // lights" purely because one title says "LED Pod", so the shop's own
    // product_type gets a vote: if both carry the same one, it is the same kind
    // of part whatever the titles say.
    const sameRawType = !!ctx.rawType && lc(p.product_type || '') === ctx.rawType;
    const redundant = (sameRawType || (!!ctx.type && pt === ctx.type)) && !counterpart;
    if (redundant) { s += W.redundant; reasons.push('same kind of part as this'); }

    // Proven attachments, never for the anchor's own category - nobody viewing
    // a spark plug wants a second spark plug.
    if (pt && pt !== ctx.type && ATTACHMENT_BIAS[pt]) {
      s += ATTACHMENT_BIAS[pt];
      reasons.push('proven attachment (' + pt + ')');
    }
    if (pt && pt !== ctx.type && PREFERRED[pt]) {
      s += PREFERRED[pt];
      reasons.push('preferred attachment (' + pt + ')');
    }

    // Domain knowledge: pads -> disc / fluid, helmet -> intercom, fog light ->
    // switch kit. Carries universal products that have no fitment tags at all.
    // The list is ordered best-first (a helmet buyer wants the intercom before
    // the visor), so the bonus tapers with position.
    let complementHit = -1;
    if (pt) {
      // "Rolon Brass Chain & Sprocket Kit" infers as 'sprocket kit' while every
      // complement list says 'chain', so the two never met and a chain kit
      // scored as though it had nothing to do with the bike it fits.
      const alias = pt === 'sprocket kit' ? 'chain'
                  : pt === 'chain'        ? 'sprocket kit' : '';
      for (let i = 0; i < ctx.complements.length; i++) {
        const c = ctx.complements[i];
        if (pt.indexOf(c) !== -1 || c.indexOf(pt) !== -1 ||
            (alias && (alias.indexOf(c) !== -1 || c.indexOf(alias) !== -1))) {
          complementHit = i; break;
        }
      }
    }
    if (complementHit >= 0 && !redundant) {
      s += Math.max(W.complement - complementHit * 2, 4);
      reasons.push('goes with this');
    }

    const v = (p.variants && p.variants[0]) || {};
    if (v.compare_at_price && parseInt(v.compare_at_price, 10) > parseInt(v.price, 10)) s += W.discount;

    return {
      score: s, reasons: reasons, isCounterpart: counterpart,
      category: pt, complementHit: complementHit >= 0,
      sharedModel: sharedModel, sharedBrand: sharedBrand,
      candNamesVehicle: candNamesVehicle,
      sameFamily: false, vehMismatch: false,
    };
  }

  // ── COLLECT ALL CANDIDATES ─────────────────────────────────────────────────
  async function fetchProducts(url, isSuggest, attempt) {
    attempt = attempt || 0;
    try {
      const r = await fetch(url);
      // Shopify throttles hard and returns an empty body, which used to look
      // exactly like "this search found nothing". Back off and try once more.
      if ((r.status === 429 || r.status === 430) && attempt < 2) {
        await new Promise(res => setTimeout(res, 400 * (attempt + 1)));
        return fetchProducts(url, isSuggest, attempt + 1);
      }
      if (!r.ok) { if (DEBUG) console.warn('[RT] ' + r.status + ' ' + url); return []; }
      const json = await r.json();
      const raw = json.products || json.resources?.results?.products || [];
      return isSuggest ? raw.map(normSuggest) : raw;
    } catch (e) { if (DEBUG) console.warn('[RT] fetch failed', url, e); return []; }
  }

  // ── REQUEST QUEUE ─────────────────────────────────────────────────────────
  // The storefront JSON endpoints are rate-limited per visitor. Measured
  // against the live store on 5 Sep 2026: three concurrent requests to
  // /search/suggest.json are fine, five or more and most come back 429 with an
  // empty body - which the old code silently read as "this search found
  // nothing". The old engine fired up to a dozen at once, so on any product
  // with several search layers most of them were being thrown away and the
  // widget fell back to whatever generic filler survived. That is a large part
  // of why the ranking looked broken on the live site.
  //
  // Everything goes through one queue: searches, recommendations and hydration.
  const MAX_CONCURRENT = 3;
  const _reqQueue = [];
  let _reqInFlight = 0;

  function _pumpRequests() {
    while (_reqInFlight < MAX_CONCURRENT && _reqQueue.length) {
      const job = _reqQueue.shift();
      _reqInFlight++;
      job.run().then(
        (res) => { _reqInFlight--; job.done(res); _pumpRequests(); },
        ()    => { _reqInFlight--; job.done(job.empty); _pumpRequests(); }
      );
    }
  }

  function queued(run, empty) {
    return new Promise((resolve) => {
      _reqQueue.push({ run: run, done: resolve, empty: empty });
      _pumpRequests();
    });
  }

  const queuedProducts = (url, isSuggest) =>
    queued(() => fetchProducts(url, isSuggest), []);

  // ── WHAT TO SEARCH FOR ────────────────────────────────────────────────────
  // Only a couple of queries are worth firing, so pick the tags that actually
  // identify the vehicle. Not every fitment tag is usable:
  //   "P85121X"        a part number
  //   "1.2 TSI"        an engine, shared across unrelated models
  //   "XL6"            a real model, too short to search safely on its own
  //   "SKODA RAPID/FABIA after 2012"   what we want
  // Storefront search is forgiving and OR-ish, so a couple of model words
  // together ("volkswagen polo vento rapid") returns the whole family.
  const ENGINE_CODE_RE = /^\d\.\d\s*(tsi|tdi|tfsi|crdi|dci|vtec|mpfi|cc)?$/;

  // Words that carry no vehicle meaning once a long fitment tag is broken up.
  const MODEL_STOPWORDS = new Set([
    'after', 'all', 'models', 'model', 'type', 'onwards', 'onward', 'present',
    'and', 'for', 'the', 'vin', 'new', 'old', 'gen', 'series', 'petrol',
    'diesel', 'only', 'upto', 'till', 'with', 'without', 'both', 'facelift',
  ]);

  // "SKODA RAPID/FABIA after 2012" + "VW POLO/POLO GT/VENTO after 2012"
  //   -> ['rapid', 'fabia', 'polo', 'vento']
  // The bare words are what match the other tagging style in this catalogue
  // (BMC tags its filters "Polo", "Vento", "Rapid" with no years at all), so
  // deriving them is what lets a Brembo pad page find the filter and the plug
  // for the same car.
  function modelWords(ctx) {
    const words = [];
    ctx.modelTags.forEach(t => {
      t.split(/[^a-z0-9.]+/).forEach(w => {
        if (w.length < 3 || MODEL_STOPWORDS.has(w)) return;
        // Keep 250 / 390 / 650 (they identify the bike), drop 2012 (a year).
        if (/^\d+(\.\d+)?$/.test(w) && w.length > 3) return;
        if (isBrandTag(w)) return;                    // makes are scored separately
        if (words.indexOf(w) === -1) words.push(w);
      });
    });
    return words;
  }

  // { specific, base } - specific is the one exact tag, base is the loose
  // model-word query. Either can be empty (a helmet has neither).
  function queryPlan(ctx) {
    const usable = ctx.modelTags.filter(t => !ENGINE_CODE_RE.test(t) && t.length >= 4);
    if (!usable.length) return { specific: '', base: '' };

    // The most specific single tag: prefer one that reads like a full model
    // line (a space or slash AND a digit), then the longest, because the more
    // specific tag is always the longer one.
    const modelish = usable.filter(t => /[\s/]/.test(t) && /\d/.test(t));
    const specific = (modelish.length ? modelish : usable)
      .slice().sort((a, b) => b.length - a.length)[0] || '';

    // Bare model tags if the product has them ("cb350rs"), otherwise the words
    // pulled out of the long ones.
    const shorts = usable.filter(t =>
      t.length <= 14 && !/[\s/]/.test(t) && !/^[a-z]{1,3}\d{3,}$/.test(t)   // not "hb184"
    ).slice(0, 3);
    const base = (shorts.length ? shorts : modelWords(ctx).slice(0, 4)).join(' ');

    return { specific: specific, base: base };
  }

  // Kept for console debugging: the vehicle queries this product would run.
  function searchTermsFor(ctx) {
    const q = queryPlan(ctx);
    return [q.specific, q.base].filter((t, i, a) => t && a.indexOf(t) === i);
  }

  // ── HYDRATION ─────────────────────────────────────────────────────────────
  // /search/suggest.json returns variants: [] - normSuggest falls back to the
  // PRODUCT id, which /cart/add.js rejects with a 422. A bundle whose button
  // cannot add its own contents is worthless, so re-fetch the handful actually
  // picked: real variant id, real price, real availability.
  async function hydratePick(p) {
    const v0 = (p.variants && p.variants[0]) || {};
    const stale = !v0.id || String(v0.id) === String(p.id) || v0.price == null;
    if (!stale || !p.handle) return p;
    try {
      const r = await queued(() => fetch('/products/' + p.handle + '.js'), null);
      if (!r) return p;
      if (!r.ok) return p;
      const full = await r.json();
      const variants = full.variants || [];
      const avail = variants.filter(v => v.available !== false);
      const v = avail[0] || variants[0];
      if (!v) return p;
      const img = full.featured_image || (full.images && full.images[0]) || '';
      return Object.assign({}, p, {
        title: full.title || p.title,
        vendor: full.vendor || p.vendor,
        product_type: full.type || p.product_type,
        featured_image: p.featured_image || (img ? { src: img } : null),
        _variantCount: variants.length,
        _available: avail.length > 0,
        variants: [{
          id: v.id,
          price: toPaise(v.price),
          compare_at_price: v.compare_at_price ? toPaise(v.compare_at_price) : null,
        }],
      });
    } catch (_) { return p; }
  }

  // ── CANDIDATES + SELECTION ────────────────────────────────────────────────
  async function findUpsells(data, wantCount = 2) {
    const ctx  = buildCtx(data);
    const plan = queryPlan(ctx);
    const rec  = '/recommendations/products.json?product_id=' +
                 encodeURIComponent(data.productId) + '&limit=10&intent=';

    if (DEBUG) console.warn('[RT] ctx', {
      type: ctx.type, axle: ctx.axle, klass: ctx.klass,
      models: ctx.modelTags, brands: ctx.brandTags, plan: plan,
      complements: ctx.complements,
    });

    // ── FAST PATH ───────────────────────────────────────────────────────────
    // Complementary products are configured store-wide now (3,363 of 3,498
    // products, curated from the same fitment rules this file uses). When that
    // list answers, it IS the answer: one request instead of ten, no searches,
    // and no hydration because /recommendations/products.json already carries
    // real variants and prices.
    //
    // This matters more than it looks. Measured on the live product page, the
    // full search path finished at 12.8s - not because the requests are slow
    // (250-900ms each) but because the page loads 212 resources and the main
    // thread is saturated, so the promise callbacks between them were starved.
    // Fewer round trips is the only fix that helps under that load.
    const curated = await queuedProducts(rec + 'complementary', false);
    if (curated && curated.length) {
      curated.forEach(p => { p.__curated = true; });
      const quick = scoreAll(curated.filter(p => String(p.id) !== String(data.productId)), ctx);
      if (quick.length >= Math.min(wantCount, 2)) {
        if (DEBUG) console.warn('[RT] curated list answered - skipping search layers');
        return selectPicks(quick, wantCount);
      }
    }

    // Every remaining request goes out at once. Priority is expressed by the
    // ORDER results are collected below, not the order they were requested in.
    const jobs = [];
    const suggest = (q, limit) => {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                  '&resources[type]=product&resources[limit]=' + (limit || 10);
      const req = queuedProducts(url, true);
      if (DEBUG) req.then(r => console.warn('[RT] search "' + q + '" -> ' + (r || []).length));
      return req;
    };

    // A. the vehicle itself - the most valuable source on this catalogue.
    //    Two shapes because two tagging styles exist: the exact tag finds the
    //    products tagged like this one, the loose model words find the rest.
    const vehicleQ = [plan.specific, plan.base].filter((t, i, a) => t && a.indexOf(t) === i);
    vehicleQ.forEach(t => jobs.push({ sameVehicle: true, req: suggest(t, 12) }));

    const probeBase = plan.base || plan.specific;
    if (probeBase) {
      // B. the vehicle + the two proven attachments. A brake pad's complements
      //    are the disc and the fluid, so nothing else would ever look for the
      //    plug or the filter for this car - and those are the two things
      //    riders actually add (5 of 8 and 3 of 8 multi-item orders).
      jobs.push({ sameVehicle: true, req: suggest(probeBase + ' spark plug air filter', 6) });

      // C. the vehicle + what goes with this part ("polo brake disc"). Keeps a
      //    big single-model family (a car with nine sets of pads) from filling
      //    every slot with substitutes.
      if (ctx.complements.length) {
        jobs.push({ sameVehicle: true, req: suggest(probeBase + ' ' + ctx.complements[0], 6) });
      }
    }

    // D. the curated list, already fetched above. Included in the pool even
    //    when it was too thin to answer on its own.
    jobs.push({ sameVehicle: false, req: Promise.resolve(curated || []) });

    // E. complement searches. The only source for universal products that carry
    //    no fitment tags at all (a helmet, a bottle of oil).
    if (!vehicleQ.length) {
      ctx.complements.slice(0, 3).forEach(c =>
        jobs.push({ sameVehicle: false, req: suggest(c, 8) })
      );
    }

    // F. algorithmic "related" = SIMILAR = mostly substitutes. Filler only.
    jobs.push({ sameVehicle: false, req: queuedProducts(rec.replace('&intent=', ''), false) });

    const results = await Promise.all(jobs.map(j => j.req));

    // Pool, highest-priority source first so the same-vehicle flag sticks.
    const pool = new Map();
    results.forEach((list, i) => {
      const sameVehicle = jobs[i].sameVehicle;
      (list || []).forEach(p => {
        if (!p || !p.id) return;
        if (String(p.id) === String(data.productId)) return;   // never the anchor
        const key = String(p.id);
        if (pool.has(key)) {
          if (sameVehicle) pool.get(key).__sameVehicle = true;  // upgrade only
          return;
        }
        p.__sameVehicle = !!sameVehicle;
        pool.set(key, p);
      });
    });

    const scored = scoreAll(pool, ctx);
    if (!scored.length) return [];
    return selectPicks(scored, wantCount);
  }

  // ── SCORING A POOL ────────────────────────────────────────────────────────
  function scoreAll(pool, ctx) {
    const scored = [], rejected = [];
    pool.forEach(p => {
      if (p.available === false) return;
      const s = scoreOne(p, ctx);
      let score = s.score;
      // A list the merchant configured in Search & Discovery beats every
      // heuristic in this file by definition, so it is ranked above an
      // incidental same-vehicle match (a Polo bumper on a Polo brake page).
      if (p.__curated && score > 0) { score += W.curated; s.reasons.push('curated for this product'); }
      if (DEBUG && score < 10) rejected.push(score + '  ' + p.title + '  [' + s.reasons.join(', ') + ']');
      // A floor, not zero: a complement hit alone is 12, a same-vehicle hit is
      // 30+. Anything below this is a guess, and a short honest bundle (or the
      // collection card) beats one padded with guesses.
      if (score < 10) return;
      scored.push(Object.assign({}, p, {
        _score: score, _reasons: s.reasons, _cat: s.category,
        _counterpart: s.isCounterpart,
      }));
    });
    scored.sort((a, b) => b._score - a._score);
    if (DEBUG) console.warn('[RT] rejected (' + rejected.length + '):', rejected);
    if (DEBUG) console.warn('[RT] scored:', scored.slice(0, 10).map(
      p => p._score + '  ' + p.title + '  [' + p._reasons.join(', ') + ']'));
    return scored;
  }

  // ── PICKING ───────────────────────────────────────────────────────────────
  async function selectPicks(scored, wantCount) {
    // Hydrate a shortlist, not the pool: real variants and prices are only
    // needed for what might actually be shown. Recommendation-sourced products
    // already carry both, so this is usually a no-op.
    const shortlist = await Promise.all(scored.slice(0, 4).map(hydratePick));

    // Products with more than one variant cannot be added blind - a helmet
    // would land in the cart in whatever size happens to be first. Penalised
    // rather than dropped, so they still show when nothing better exists.
    shortlist.forEach(p => {
      p._rank = p._score + ((p._variantCount || 1) > 1 ? W.multiVariant : 0);
    });
    shortlist.sort((a, b) => b._rank - a._rank);

    const picks = [], perVendor = {}, usedCat = {};
    let counterparts = 0;
    for (let i = 0; i < shortlist.length && picks.length < wantCount; i++) {
      const p = shortlist[i];
      if (p._available === false) continue;

      const vend = lc(p.vendor || '?');
      if ((perVendor[vend] || 0) >= 2) continue;      // one brand can't take the row

      // A bundle showing two brake discs looks broken even when both score
      // well. One of each kind of part - except the axle counterpart, which is
      // the whole point of the pairing, and only ever one of those.
      if (p._counterpart) {
        if (counterparts >= 1) continue;
        counterparts++;
      } else if (p._cat) {
        if (usedCat[p._cat]) continue;
        usedCat[p._cat] = 1;
      }

      perVendor[vend] = (perVendor[vend] || 0) + 1;
      picks.push(p);
    }

    if (DEBUG) console.warn('[RT] picks:', picks.map(p => p._rank + '  ' + p.title));
    return picks;
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  function render(upsells, d, collection) {
    const root = document.getElementById('rt-smart-upsell-root');
    if (!root) return;
    if (!upsells.length && !collection) return;  // nothing to show → caller collapses
    root.classList.remove('rt-up-empty');

    const curP = d.productPrice;
    const ups  = upsells.map(u => {
      const v   = u.variants?.[0] || {};
      const p   = parseInt(v.price) || 0;
      const cp  = parseInt(v.compare_at_price) || 0;
      return { ...u, _p: p, _cp: cp, _save: cp > p ? cp - p : 0, _vid: v.id };
    });

    const totalSave = ups.reduce((a, u) => a + u._save, 0);
    const totalP    = curP + ups.reduce((a, u) => a + u._p, 0);
    const itemCount = 1 + ups.length;

    const upsellRow = (u, idx) => {
      const benefit = getBenefit(u);
      const imgSrc =
        u.featured_image?.src ||
        u.featured_image?.url ||
        (typeof u.featured_image === 'string' ? u.featured_image : '') ||
        u.images?.[0]?.src || '';
      const productUrl = `/products/${u.handle}`;
      return `
      <div class="rt-up__row rt-up__row--add" id="rt-up-addon-${idx}">
        <label class="rt-up__chk-label" onclick="event.stopPropagation()">
          <input type="checkbox" id="rt-up-cb-${idx}" class="rt-up__cb-hidden rt-up__cb" data-idx="${idx}" checked>
          <span class="rt-up__chk rt-up__chk--on" id="rt-up-chk-${idx}">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>
          </span>
        </label>
        <a href="${productUrl}" class="rt-up__product-link" data-handle="${u.handle}" onclick="event.stopPropagation()">
          ${imgSrc
            ? `<img class="rt-up__img" src="${rtImg(imgSrc, 96)}" alt="${esc(u.title)}" loading="lazy" onerror="this.outerHTML='<div class=\\'rt-up__img rt-up__img--ph\\'></div>'">`
            : `<div class="rt-up__img rt-up__img--ph"></div>`}
        </a>
        <div class="rt-up__info rt-up__info--link" data-product-url="${productUrl}">
          <span class="rt-up__name">${esc(u.title)}</span>
          ${benefit ? `<span class="rt-up__benefit">${benefit}</span>` : ''}
          <div class="rt-up__meta">
            ${u._save > 0 ? `<span class="rt-up__pill">SAVE ${fmt(u._save)}</span>` : ''}
            <span class="rt-up__sub">${esc(u.vendor || '')}</span>
          </div>
        </div>
        <div class="rt-up__pricestack">
          <span class="rt-up__price">${fmt(u._p)}</span>
          ${u._save > 0 ? `<span class="rt-up__was">${fmt(u._cp)}</span>` : ''}
        </div>
      </div>`;
    };

    const hasBundle = ups.length > 0;

    const headHTML = `
  <div class="rt-up__head">
    <span class="rt-up__dot"></span>
    <strong class="rt-up__title">${(hasBundle ? pick(HEADINGS) : 'Complete the build').toUpperCase()}</strong>
    <span class="rt-up__social">
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
        <circle cx="5.5" cy="5.5" r="5" stroke="#22c55e" stroke-width="1.2"/>
        <path d="M2.5 5.5l2.3 2.3L9 3" stroke="#22c55e" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${rnd(28,97)} ${pick(SOCIAL_COPY)}</span>
    </span>
  </div>`;

    const anchorHTML = `
    <div class="rt-up__row">
      <span class="rt-up__chk rt-up__chk--on rt-up__chk--lock">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>
      </span>
      <img class="rt-up__img" src="${rtImg(d.productImage, 96)}" alt="" loading="lazy" onerror="this.outerHTML='<div class=\\'rt-up__img rt-up__img--ph\\'></div>'">
      <div class="rt-up__info">
        <span class="rt-up__name">${esc(d.productTitle)}</span>
        <span class="rt-up__sub">${d.productVendor ? esc(d.productVendor) + ' · ' : ''}This product</span>
      </div>
      <span class="rt-up__price">${fmt(curP)}</span>
    </div>`;

    // Collection CTA - "Explore more in …" link (hybrid). Solo variant is
    // the prominent browse card shown when there's no confident bundle.
    const browseVerb = (collection && collection.cta) || (hasBundle ? 'Explore more in' : 'Browse');
    const browseHTML = collection ? `
  <a class="rt-up__browse${hasBundle ? '' : ' rt-up__browse--solo'}" href="/collections/${collection.handle}">
    <span class="rt-up__browse-txt">${browseVerb} <strong>${collection.label}</strong></span>
    <span class="rt-up__browse-arrow" aria-hidden="true">→</span>
  </a>` : '';

    const bundleHTML = `
  <div class="rt-up__items">
    ${anchorHTML}
    ${ups.map((u, i) => upsellRow(u, i)).join('')}
  </div>

  ${totalSave > 0 ? `
  <div class="rt-up__save-row">
    <div class="rt-up__save-left">
      <div class="rt-up__save-lbl">YOU SAVE</div>
      <div class="rt-up__save-amt" id="rt-up-save-amt">${fmt(totalSave)}</div>
    </div>
    <div class="rt-up__save-right">
      <div class="rt-up__save-track"><div class="rt-up__save-bar"></div></div>
      <span class="rt-up__save-note">Great deal! You're saving on this kit.</span>
    </div>
  </div>` : ''}

  <div class="rt-up__reward" id="rt-up-reward"></div>

  <div class="rt-up__foot">
    <div class="rt-up__tot-wrap">
      <div class="rt-up__tot-lbl">BUNDLE TOTAL (<span id="rt-up-cnt">${itemCount}</span> ITEMS)</div>
      <div class="rt-up__tot-price" id="rt-up-tot">${fmt(totalP)}</div>
    </div>
    <button class="rt-up__btn" id="rt-up-btn">Add ${itemCount} items to cart</button>
  </div>
  ${browseHTML}`;

    const soloHTML = `
  <div class="rt-up__items">
    ${anchorHTML}
  </div>
  ${browseHTML}`;

    root.innerHTML = `
<div class="rt-up${hasBundle ? '' : ' rt-up--solo'}">
  ${headHTML}
  ${hasBundle ? bundleHTML : soloHTML}
</div>`;

    // Fade in the new content smoothly
    const rtUpEl = root.querySelector('.rt-up');
    if (rtUpEl) {
      rtUpEl.style.opacity = '0';
      rtUpEl.style.transform = 'translateY(4px)';
      rtUpEl.style.transition = 'none';
      // Force reflow so transition triggers
      void rtUpEl.offsetHeight;
      rtUpEl.style.transition = 'opacity 0.14s ease, transform 0.14s ease';
      requestAnimationFrame(function() {
        rtUpEl.style.opacity = '1';
        rtUpEl.style.transform = 'translateY(0)';
      });
    }

    // ── Conflict shield ───────────────────────────────────────────────────────
    // The theme's custom-async.js binds a delegated `openUpsell` click handler
    // to an ancestor <div>; it throws (null.querySelectorAll) on clicks inside
    // our widget and was swallowing the click. Stop our clicks from bubbling
    // out to it. Guarded so re-renders don't stack listeners.
    if (!root.dataset.rtClickShield) {
      root.addEventListener('click', function (e) { e.stopPropagation(); });
      root.dataset.rtClickShield = '1';
    }

    // Collection CTA - navigate explicitly (don't rely on default <a> nav,
    // which the theme/extension handlers can intercept). Works in both modes.
    const browseLink = root.querySelector('.rt-up__browse');
    if (browseLink) {
      browseLink.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(browseLink.getAttribute('href'));
      });
    }

    // Solo (collection-only) card has no bundle controls - nothing to wire up.
    if (!ups.length) return;

    // ── Interactions ──────────────────────────────────────────────────────────
    const btn       = document.getElementById('rt-up-btn');
    const totEl     = document.getElementById('rt-up-tot');
    const cntEl     = document.getElementById('rt-up-cnt');
    const saveAmtEl = document.getElementById('rt-up-save-amt');
    const rewardEl  = document.getElementById('rt-up-reward');
    const REWARD_CHECK = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="6" fill="currentColor" opacity=".18"/><path d="M3.6 6.6l2 2 3.8-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const REWARD_INFO  = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="5.6" stroke="currentColor" stroke-width="1.3"/><path d="M6.5 3.6v3.4M6.5 9.2h.01" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

    function recalc() {
      let total = curP, count = 1, save = 0;
      ups.forEach((u, i) => {
        const cb = document.getElementById(`rt-up-cb-${i}`);
        if (cb?.checked) {
          total += u._p;
          save  += u._save;
          count++;
        }
      });

      const unlocked  = count >= REWARD.minItems;          // Triple Reward active?
      const rewardCut = unlocked ? Math.round(total * REWARD.pct / 100) : 0;
      const payNow    = total - rewardCut;                 // actual price after the 3%

      // Bundle total = what they actually pay. When unlocked, show the
      // discounted figure (matches the cart) with the pre-discount struck out.
      totEl.innerHTML = unlocked
        ? money(payNow) + ' <s class="rt-up__tot-was">' + money(total) + '</s>'
        : money(total);
      cntEl.textContent = count;
      btn.textContent   = `Add ${count} item${count > 1 ? 's' : ''} to cart`;
      if (saveAmtEl) saveAmtEl.textContent = money(save + rewardCut);

      // Triple Reward strip - no fixed rupee figure (the 3% scales, it's not a cap)
      if (rewardEl) {
        if (unlocked) {
          rewardEl.className = 'rt-up__reward is-unlocked';
          rewardEl.innerHTML = REWARD_CHECK +
            '<span><strong>' + REWARD.pct + '% OFF unlocked</strong> - ' + REWARD.label + ' applied at checkout</span>';
        } else {
          const need = REWARD.minItems - count;
          rewardEl.className = 'rt-up__reward';
          rewardEl.innerHTML = REWARD_INFO +
            '<span>Add <strong>' + need + ' more</strong> to unlock <strong>' + REWARD.pct + '% OFF</strong> - ' + REWARD.label + '</span>';
        }
      }
    }

    recalc();  // initialise reward + totals to match the rendered state

    // Checkbox toggle - only the checkbox label area
    document.querySelectorAll('.rt-up__cb').forEach((cb, i) => {
      cb.addEventListener('change', () => {
        const chk = document.getElementById(`rt-up-chk-${i}`);
        chk?.classList.toggle('rt-up__chk--on', cb.checked);
        // Also dim the row when unchecked
        const row = document.getElementById(`rt-up-addon-${i}`);
        row?.classList.toggle('rt-up__row--unchecked', !cb.checked);
        recalc();
      });
    });

    // Clicking the product info area toggles checkbox (not opens page)
    // Clicking the image/title link opens the product page
    document.querySelectorAll('.rt-up__row--add').forEach((row, i) => {
      // Click anywhere on the row (except checkbox label and product link) toggles checkbox
      row.addEventListener('click', (e) => {
        if (e.target.closest('.rt-up__chk-label') || e.target.closest('.rt-up__product-link')) return;
        const cb = document.getElementById(`rt-up-cb-${i}`);
        if (cb) {
          cb.checked = !cb.checked;
          cb.dispatchEvent(new Event('change'));
        }
      });
    });

    // Add to cart - adds the current product + every checked upsell in ONE
    // call, then sends the rider to /cart. This theme has no cart drawer and
    // no [name="add"] button the old code relied on, so we drive the AJAX
    // Cart API directly and redirect (the expected feedback).
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const items = [];
        const anchorVid = getSelectedVariantId();   // current product's selected variant
        if (anchorVid) items.push({ id: parseInt(anchorVid, 10), quantity: 1 });

        // Resolve each checked upsell to a real VARIANT id. Search-sourced
        // upsells (/search/suggest.json) omit variant data, so _vid can fall
        // back to the PRODUCT id → /cart/add.js 422 "Cannot find variant".
        // Re-fetch the product JSON to get the true first-variant id.
        const chosen = ups.filter((u, i) => document.getElementById(`rt-up-cb-${i}`)?.checked);
        const vids = await Promise.all(chosen.map(async (u) => {
          let vid = u._vid;
          if (!vid || String(vid) === String(u.id)) {
            try {
              const r = await fetch(`/products/${u.handle}.js`);
              if (r.ok) { const pj = await r.json(); vid = pj.variants && pj.variants[0] && pj.variants[0].id; }
            } catch (_) {}
          }
          return vid;
        }));
        vids.forEach((vid) => { if (vid) items.push({ id: parseInt(vid, 10), quantity: 1 }); });

        if (!items.length) { btn.disabled = false; recalc(); return; }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);

        btn.textContent = '✓ Added!';
        // Redirect to the cart so the rider sees the full kit.
        window.location.assign('/cart');
      } catch (e) {
        console.warn('[RT Upsell]', e);
        btn.textContent = 'Try again';
        btn.disabled = false;
        setTimeout(recalc, 1500);
      }
    });
  }  // end render

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:open',    { bubbles: true }));
    fetch('/cart.js').then(r => r.json()).then(c => {
      document.querySelectorAll(
        '.cart-count,[data-cart-count],.header__cart-count,.cart__count'
      ).forEach(el => { el.textContent = c.item_count; });
    }).catch(() => {});
  }

  // ── Fetch product handles and return normalized picks ─────────────────────
  async function fetchHandlePicks(entries) {
    const results = await Promise.all(
      entries.map(async entry => {
        if (!entry.startsWith('handle:')) return null;
        const handle = entry.slice(7);
        try {
          const r = await fetch(`/products/${handle}.js`);
          if (!r.ok) return null;
          const p = await r.json();
          const v = p.variants?.[0] || {};
          return {
            ...p,
            _score: 999,
            featured_image: p.featured_image || (p.images?.[0] ? { src: p.images[0].src } : null),
            variants: [{ id: v.id, price: toPaise(v.price), compare_at_price: v.compare_at_price ? toPaise(v.compare_at_price) : null }]
          };
        } catch (_) { return null; }
      })
    );
    return results.filter(Boolean);
  }

  // Get current selected variant title using RTUpsellData.productVariants
  // Xtra updates the URL with ?variant=ID - look it up from the variants array
  function getSelectedVariantTitle() {
    const d = window.RTUpsellData;
    const variants = d?.productVariants || [];

    // Get variant ID from URL
    const vid = new URLSearchParams(window.location.search).get('variant');
    if (vid && variants.length) {
      const match = variants.find(v => String(v.id) === String(vid));
      if (match) return match.title;
    }

    // Fallback: checked radio input in variant selector
    const checked = document.querySelector('.f8pr input[type="radio"]:checked, form[action*="/cart/add"] input[type="radio"]:checked');
    if (checked) {
      const label = checked.closest('label') || document.querySelector(`label[for="${checked.id}"]`);
      if (label) return label.textContent.trim();
    }

    // Fallback: selected option in a select
    const sel = document.querySelector('select[name="id"], .f8pr select');
    if (sel) return sel.options[sel.selectedIndex]?.text?.trim() || '';

    // Fallback: first variant
    return variants[0]?.title || '';
  }

  // ── Match variant ID against variant map ──────────────────────────────────
  // Keys are comma-separated variant IDs (no title lookup needed)
  function matchVariantMap(variantId, variantMap) {
    if (!variantMap || !variantId) return null;
    const vid = String(variantId);
    for (const [keys, handles] of Object.entries(variantMap)) {
      const ids = keys.split(',').map(k => k.trim());
      if (ids.includes(vid)) return handles;
    }
    return null;
  }

  // Get current variant ID - Xtra-aware
  // Xtra does NOT update the URL (?variant=) on variant change (only xclusive theme does).
  // Instead Xtra updates:
  //   1. [data-current-variant] on .f8pr-variant-selection (via AJAX section re-render)
  //   2. input[name="id"].product-variant-id hidden input
  //   3. input[name="variant-id"]:checked radio
  // Priority: DOM attribute > hidden input > URL > first variant fallback
  function getSelectedVariantId() {
    // P1: Xtra's AJAX-updated attribute (most reliable - set after every variant swap)
    const varSel = document.querySelector('[data-current-variant]');
    if (varSel?.dataset?.currentVariant) return String(varSel.dataset.currentVariant);

    // P2: Hidden input Xtra keeps in sync
    const hiddenInput = document.querySelector('input[name="id"].product-variant-id');
    if (hiddenInput?.value) return String(hiddenInput.value);

    // P3: Checked radio button (name="variant-id")
    const checked = document.querySelector('input[name="variant-id"]:checked');
    if (checked?.value) return String(checked.value);

    // P4: URL param (only present on direct deep-link, not after Xtra's AJAX swap)
    const urlVid = new URLSearchParams(window.location.search).get('variant');
    if (urlVid) return urlVid;

    // P5: Any hidden id input in cart form
    const anyInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (anyInput?.value) return String(anyInput.value);

    return String(window.RTUpsellData?.productFirstVariantId || '');
  }

  async function init() {
    const d = window.RTUpsellData;
    if (!d?.productId) return;

    const root = document.getElementById('rt-smart-upsell-root');
    const upsellAttr = root?.dataset?.upsell || '';
    if (upsellAttr) {
      d.manualUpsellEntries = upsellAttr.split(',').map(s => s.trim()).filter(Boolean);
    }

    // variantUpsellMap is built by Liquid at render time and injected directly
    // into window.RTUpsellData - no JSON parsing needed, no data-attribute reading.
    // Format: { "variantId": ["handle:exhaust", "handle:filter", ...], ... }
    // Already available as d.variantUpsellMap from the Liquid <script> block.

    // ── productVariants fallback - fetch from product JSON if Liquid cache is stale ──
    if (!d.productVariants?.length && d.productHandle) {
      try {
        const r = await fetch(`/products/${d.productHandle}.js`);
        const p = await r.json();
        d.productVariants = (p.variants || []).map(v => ({ id: v.id, title: v.title }));
        if (DEBUG) console.warn('[RT] productVariants fetched:', d.productVariants.length);
      } catch (_) {}
    }

    // ── Run upsell logic (variant-aware) ────────────────────────────────────
    async function runUpsell(overrideVid) {
      // Best collection to surface as the "Explore more in …" CTA (hybrid).
      const collection = collectionFor(d);

      // L-1: Liquid-built variantUpsellMap - direct object lookup, zero parsing.
      // Liquid already matched every variant title/ID to its handles at render time.
      // overrideVid: variant ID from radio click (Hook B) before Xtra AJAX completes.
      if (d.variantUpsellMap && Object.keys(d.variantUpsellMap).length) {
        const vid = String(overrideVid || getSelectedVariantId());
        if (DEBUG) console.warn('[RT] variantUpsellMap lookup, vid:', vid, overrideVid ? '(radio)' : '(DOM)');
        const handles = d.variantUpsellMap[vid];
        if (handles && handles.length) {
          if (DEBUG) console.warn('[RT] ✓ Hit:', handles);
          const picks = await fetchHandlePicks(handles);
          if (picks.length) { render(picks, d, collection); return; }
        } else {
          if (DEBUG) console.warn('[RT] No map entry for', vid, '- keys:', Object.keys(d.variantUpsellMap));
        }
      }

      // L0: static handle overrides
      if (d.manualUpsellEntries?.length) {
        const picks = await fetchHandlePicks(d.manualUpsellEntries);
        if (picks.length) { render(picks, d, collection); return; }
      }

      // Algorithm fallback - render bundle if found, else the collection-only
      // card so the slot still helps the rider discover the right category.
      const upsells = await findUpsells(d, 3);   // anchor + 3 = a 4-item kit
      if (upsells.length || collection) { render(upsells, d, collection); return; }

      // RT PERF (CLS): nothing to show at all - collapse the reserved space.
      const rootEl = document.getElementById('rt-smart-upsell-root');
      if (rootEl && !rootEl.querySelector('.rt-up')) rootEl.classList.add('rt-up-empty');
    }

    try {
      await runUpsell();
    } catch (e) { console.warn('[RT Upsell] init error:', e); }

    // ── Listen for variant changes - Xtra-native detection ────────────────────
    // Xtra does NOT update the URL on variant change (only xclusive does).
    // Two primary hooks + one safety-net cover every variant interaction:
    //
    // Hook A - MutationObserver on .f8pr-variant-selection parent
    //   Xtra’s AJAX re-render replaces the .f8pr-variant-selection element.
    //   The new element carries data-current-variant="<new_id>".
    //   We watch for that attribute appearing in the DOM.
    //
    // Hook B - delegated change event on radio/select inputs
    //   Fires the instant the rider clicks a variant button.
    //   Debounced 80ms so the AJAX swap can complete and P1 is readable.
    //
    // Hook C - lightweight 800ms interval safety-net
    //   Catches keyboard navigation / programmatic variant changes.
    //
    // All hooks share one debounced handler - no stacked fetches.

    let _lastVid = getSelectedVariantId();
    let _runTimer = null;

    async function _onVariantChange(forcedVid) {
      const cur = forcedVid || getSelectedVariantId();
      if (cur === _lastVid) return;
      _lastVid = cur;
      if (DEBUG) console.warn('[RT] Variant changed →', cur, forcedVid ? '(from radio)' : '(from DOM)');

      // Smooth crossfade: fade out current content while fetching new products.
      // New content fades in after fetch - no jarring wipe or loading flash.
      const root2 = document.getElementById('rt-smart-upsell-root');
      const inner = root2 && root2.querySelector('.rt-up');
      if (inner) {
        inner.style.transition = 'opacity 0.18s ease';
        inner.style.opacity = '0.35';
        inner.style.pointerEvents = 'none';
      }
      try { await runUpsell(cur); } catch (_) {}
    }

    function _scheduleVariantChange(forcedVid) {
      clearTimeout(_runTimer);
      _runTimer = setTimeout(function() { _onVariantChange(forcedVid); }, 80);
    }

    // Hook A: MutationObserver - fires after Xtra’s AJAX DOM swap
    const _observeTarget =
      document.querySelector('#main-product .m6pr, .m6pr, form.f8pr')?.parentElement ||
      document.querySelector('#main-product, .m6pr') ||
      document.body;

    const _variantObserver = new MutationObserver(function(mutations) {
      const relevant = mutations.some(function(m) {
        // Attribute mutation directly on [data-current-variant]
        if (m.type === 'attributes' && m.attributeName === 'data-current-variant') return true;
        // Node added that contains or IS [data-current-variant]
        return Array.from(m.addedNodes).some(function(n) {
          return n.nodeType === 1 && (
            n.hasAttribute('data-current-variant') ||
            n.querySelector('[data-current-variant]')
          );
        });
      });
      if (relevant) _scheduleVariantChange();
    });

    _variantObserver.observe(_observeTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-current-variant'],
    });

    // Hook B: delegated change event - survives Xtra DOM replacement.
    //
    // Xtra renders variants in TWO different ways depending on product.options.size:
    //
    // A) name="variant-id" (multi-option products or dropdown style)
    //    input.value = numeric variant ID → use directly as forcedVid
    //
    // B) name="options[sectionId-OptionName]" (single-option products: options.size == 1)
    //    input.value = TEXT label e.g. "Hunter 350 (2022-2024)" → NOT a variant ID
    //    Must resolve to numeric ID via productVariants title lookup.
    //
    // In both cases [data-current-variant] still holds the OLD id at change time
    // (Xtra’s AJAX swap takes 200–500ms), so we cannot read from DOM.
    function _resolveVariantId(inputName, inputValue) {
      // Case A: direct numeric ID
      if (inputName === 'variant-id' || inputName === 'id') {
        return String(inputValue || '');
      }
      // Case B: options[...] - text value, resolve via title match
      if (inputName && inputName.startsWith('options[')) {
        const variants = window.RTUpsellData?.productVariants || [];
        const title = String(inputValue || '').trim().toLowerCase();
        const match = variants.find(v => v.title.trim().toLowerCase() === title);
        if (match) {
          if (DEBUG) console.warn('[RT] Resolved "' + inputValue + '" → variant ID', match.id);
          return String(match.id);
        }
        // Partial match fallback (handles year ranges in title)
        const partial = variants.find(v => {
          const vt = v.title.trim().toLowerCase();
          return vt.includes(title) || title.includes(vt);
        });
        if (partial) {
          if (DEBUG) console.warn('[RT] Partial match "' + inputValue + '" → variant ID', partial.id);
          return String(partial.id);
        }
        if (DEBUG) console.warn('[RT] Could not resolve variant title:', inputValue, 'variants:', variants.map(v=>v.title));
      }
      return '';
    }

    document.addEventListener('change', function(e) {
      const t = e.target;
      if (
        t.matches('input[name="variant-id"]') ||
        t.matches('select[name="variant-id"]') ||
        t.matches('input[name="id"].product-variant-id') ||
        (t.matches('input[type="radio"], select') && t.name && t.name.startsWith('options['))
      ) {
        const forcedVid = _resolveVariantId(t.name, t.value);
        if (DEBUG) console.warn('[RT] Hook B change, name:', t.name, 'value:', t.value, '→ forcedVid:', forcedVid);
        if (forcedVid) _scheduleVariantChange(forcedVid);
      }
    });

    // Hook C: 800ms safety-net interval (catches keyboard nav / programmatic changes)
    setInterval(function() {
      if (document.hidden) return; // RT FIX (INP): skip main-thread work on backgrounded tabs
      const cur = getSelectedVariantId();
      if (cur !== _lastVid) _scheduleVariantChange();
    }, 800);
  }

  // Exposed for debugging in the console and for the offline test harness.
  // Nothing on the page reads this.
  window.RTUpsell = {
    findUpsells: findUpsells, scoreOne: scoreOne, buildCtx: buildCtx,
    fitmentTagsOf: fitmentTagsOf, searchTermsFor: searchTermsFor,
    axleOf: axleOf, typeOf: typeOf, namesAVehicle: namesAVehicle,
    tagOverlap: tagOverlap, tagsMatch: tagsMatch,
    hydratePick: hydratePick, WEIGHTS: W,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 200);
  }
})();